import axios from 'axios';
import {
  activateMaintenanceMode,
  areMaintenanceWritesBlocked,
  isMaintenancePayload,
  isWriteMethod,
} from '@/maintenance';
import type {
  CreatureStatblockRecallKnowledge,
  CreatureStatblockResponse,
} from './generated/creatureStatblockV3';

export type {
  CreatureStatblockItem,
  CreatureStatblockRecallKnowledge,
  CreatureStatblockSpellcastingItem,
  CreatureStatblockStrike,
} from './generated/creatureStatblockV3';
export {
  CREATURE_STATBLOCK_SCHEMA_VERSION,
} from './generated/creatureStatblockV3';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Создаём базовый экземпляр axios
export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

type AuthBroadcastEvent = 'login' | 'logout';

let accessTokenMemory: string | null = null;

const authChannel = (() => {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return null;
  }
  return new BroadcastChannel('pf2e-auth');
})();

const broadcastAuth = (event: AuthBroadcastEvent): void => {
  authChannel?.postMessage({ type: event });
};

export const onAuthBroadcast = (handler: (event: AuthBroadcastEvent) => void): (() => void) => {
  if (!authChannel) return () => {};
  const listener = (event: MessageEvent<{ type?: AuthBroadcastEvent }>) => {
    if (event.data?.type === 'login' || event.data?.type === 'logout') {
      handler(event.data.type);
    }
  };
  authChannel.addEventListener('message', listener);
  return () => authChannel.removeEventListener('message', listener);
};

export const getAccessToken = (): string | null => accessTokenMemory;

export const hasAccessToken = (): boolean => Boolean(accessTokenMemory);

const removeLegacyStoredTokens = (): void => {
  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  } catch {
    // Ignore storage failures: tokens must no longer live in JS-readable storage.
  }
};

export const setAccessToken = (accessToken: string, options: { broadcast?: boolean } = {}): void => {
  accessTokenMemory = accessToken;
  removeLegacyStoredTokens();
  if (options.broadcast) broadcastAuth('login');
};

export const clearAccessToken = (options: { broadcast?: boolean } = {}): void => {
  accessTokenMemory = null;
  removeLegacyStoredTokens();
  if (options.broadcast) broadcastAuth('logout');
};

if (typeof window !== 'undefined') {
  removeLegacyStoredTokens();
}

// Короткий correlation id для трейсинга запроса в логах backend (X-Correlation-ID).
const makeCorrelationId = (): string => {
  try {
    return crypto.randomUUID().slice(0, 8);
  } catch {
    return Math.random().toString(16).slice(2, 10);
  }
};

// Интерцептор для добавления токена
api.interceptors.request.use(
  (config) => {
    if (areMaintenanceWritesBlocked() && isWriteMethod(config.method)) {
      return Promise.reject(new DOMException('maintenance_in_progress', 'InvalidStateError'));
    }
    const token = getAccessToken();
    const hasExplicitAuthorization = Boolean(config.headers.Authorization);
    if (token && !hasExplicitAuthorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['X-Correlation-ID'] = makeCorrelationId();
    return config;
  },
  (error) => Promise.reject(error)
);

// 401 = токен реально невалиден (чистим). Транзиентная ошибка (нет ответа /
// network / timeout / 5xx) НЕ должна стирать валидные токены — иначе моргнувший
// бэкенд разлогинивает пользователя (C19 L1, находка 6).
export const isTokenInvalidError = (err: unknown): boolean =>
  axios.isAxiosError(err) && err.response?.status === 401;

// Single-flight refresh, общий для response-интерцептора и silent-bootstrap
// (authStore.checkAuth). Cross-tab сериализация через Web Locks API закрывает
// гонку ротации refresh-токена: при одновременном старте нескольких вкладок
// каждая иначе шлёт один и тот же refresh-cookie параллельно — первая ротирует
// сессию, остальные ловят "session_not_found" → ложный logout. Lock заставляет
// поздних вызывающих читать уже ротированный cookie, а не устаревший.
let inFlightRefresh: Promise<string> | null = null;

const performRefresh = async (): Promise<string> => {
  const response = await axios.post<AuthResponse>(`${API_URL}/auth/refresh`, undefined, {
    withCredentials: true,
  });
  setAccessToken(response.data.access_token);
  return response.data.access_token;
};

const withCrossTabRefreshLock = <T>(run: () => Promise<T>): Promise<T> => {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (locks?.request) {
    // lib.dom типизирует callback как синхронный `() => T`, поэтому возврат
    // Promise оборачивается в Promise<Promise<T>>; рантайм его авто-сплющивает.
    return locks.request('pf2e-auth-refresh', run) as unknown as Promise<T>;
  }
  return run();
};

export const refreshAccessToken = (): Promise<string> => {
  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = withCrossTabRefreshLock(performRefresh).finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
};

// Флаг для предотвращения множественных запросов на обновление
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Интерцептор для обработки ошибок и обновления токена
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 503 && isMaintenancePayload(error.response?.data)) {
      activateMaintenanceMode();
      return Promise.reject(error);
    }

    // 401 на публичных шагах входа означает неверные credentials/2FA, а не
    // истёкший access-токен. Refresh здесь маскировал исходную auth-ошибку и
    // мешал форме корректно предложить повторный ввод OTP.
    const isLoginRequest = originalRequest?.url === '/auth/login'
      || originalRequest?.url === '/auth/login/2fa';
    if (error.response?.status === 401 && isLoginRequest) {
      return Promise.reject(error);
    }

    // Если ошибка 401 и мы ещё не пробовали обновить токен
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest.url?.includes('/auth/refresh')) {
        // Если это уже запрос на обновление токена - просто очищаем access.
        // Refresh живёт в HttpOnly-cookie и удаляется backend'ом.
        clearAccessToken({ broadcast: true });
        // Не делаем редирект - пользователь может продолжать работу без авторизации
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Если уже идёт обновление токена - ставим в очередь
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
          })
          .then(() => {
            originalRequest.headers.Authorization = `Bearer ${getAccessToken()}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const access_token = await refreshAccessToken();

        // Обрабатываем очередь
        processQueue(null);

        // Повторяем оригинальный запрос с новым токеном
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Токены чистим ТОЛЬКО если refresh реально отверг сессию (401).
        // При network/timeout/5xx оставляем токены — сбой бэка не разлогинивает.
        if (isTokenInvalidError(refreshError)) {
          clearAccessToken({ broadcast: true });
        }
        processQueue(refreshError as Error, null);
        // Не делаем редирект - пользователь может продолжать работу без авторизации
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ==================== Auth API ====================

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
}

export interface TwoFactorRequiredResponse {
  requires_2fa: boolean;
  detail: string;
  temp_token: string;
}

export interface Login2FARequest {
  temp_token: string;
  two_factor_code: string;
}

export interface ForcePasswordChangeRequest {
  new_email: string;
  new_password: string;
  confirm_password: string;
  two_factor_code: string;
}

export interface TwoFactorSetupResponse {
  secret: string;
  qr_code_url: string;
  manual_entry_key: string;
}

export interface RequiredTwoFactorSetupCompleteResponse extends AuthResponse {
  must_change_password: boolean;
}

export interface UserResponse {
  id: string;
  email: string;
  is_active: boolean;
  is_verified: boolean;
  is_admin: boolean;
  is_game_master: boolean;
  is_localizator: boolean;
  created_at: string;
  last_login_at: string | null;
  must_change_password: boolean;
  two_factor_enabled: boolean;
  must_setup_two_factor: boolean;
  show_english_names: boolean;
}

export const authApi = {
  register: (data: RegisterRequest) => api.post<UserResponse>('/auth/register', data),

  login: async (data: LoginRequest) => {
    // Для совместимости с OAuth2PasswordRequestForm отправляем как form-data
    const formData = new FormData();
    formData.append('username', data.email); // OAuth2 использует 'username' для email
    formData.append('password', data.password);

    const response = await api.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // Проверяем что вернул сервер - может быть 2FA ответ
    if (response.data.requires_2fa) {
      return { requires2FA: response.data as TwoFactorRequiredResponse };
    }

    // Проверяем нужно ли менять пароль - если да, НЕ сохраняем токены!
    // Но используем временный токен для загрузки данных пользователя
    const tempToken = response.data.access_token;
    const userResponse = await api.get<UserResponse>('/auth/me', {
      headers: {
        'Authorization': `Bearer ${tempToken}`,
      },
    });

    if (userResponse.data.must_change_password) {
      // НЕ сохраняем токены - пользователь должен сначала сменить пароль!
      // Возвращаем временный токен для использования на форме смены пароля
      return {
        ...response,
        mustChangePassword: true,
        tempToken: tempToken  // Временный токен для доступа к форме
      };
    }

    if (userResponse.data.must_setup_two_factor) {
      // Не сохраняем access: он разрешает только настройку новой 2FA-привязки.
      return {
        ...response,
        mustSetupTwoFactor: true,
        tempToken,
      };
    }

    // Сохраняем access только если не требуется смена пароля.
    // Refresh живёт в HttpOnly cookie и не доступен JavaScript.
    setAccessToken(response.data.access_token, { broadcast: true });

    return response;
  },

  login2FA: async (data: Login2FARequest) => {
    const response = await api.post<AuthResponse>('/auth/login/2fa', data);
    // Сохраняем access только после успешного 2FA.
    setAccessToken(response.data.access_token, { broadcast: true });
    return response;
  },

  setup2FA: (config?: any) => api.get<TwoFactorSetupResponse>('/auth/2fa/setup', config),

  completeRequired2FASetup: async (twoFactorCode: string, config?: any) => {
    const response = await api.post<RequiredTwoFactorSetupCompleteResponse>(
      '/auth/2fa/required-setup/complete',
      { two_factor_code: twoFactorCode },
      config,
    );
    if (response.data.access_token && !response.data.must_change_password) {
      setAccessToken(response.data.access_token, { broadcast: true });
    }
    return response;
  },

  forcePasswordChange: async (data: ForcePasswordChangeRequest, config?: any) => {
    const response = await api.post<UserResponse & AuthResponse>('/auth/force-password-change', data, config);
    if (response.data.access_token) {
      setAccessToken(response.data.access_token, { broadcast: true });
    }
    return response;
  },

  logout: () => {
    api.post('/auth/logout', undefined, { withCredentials: true }).catch(() => {
      // Игнорируем ошибки logout
    });
    clearAccessToken({ broadcast: true });
  },

  refresh: async () => {
    // Идёт через общий single-flight + cross-tab lock (refreshAccessToken),
    // чтобы bootstrap одной вкладки не гонялся с ротацией в другой.
    return refreshAccessToken();
  },

  getCurrentUser: (config?: any) => api.get<UserResponse>('/auth/me', config),
};

// ==================== Character API ====================

export interface SpellbookEntry {
  id: string;
  spell_id: string;
  name: string;
  localized_name?: string | null;
  slug: string | null;
  spell_rank: number;
  spell_type: string | null;
  localized_spell_type?: string | null;
  tradition: string | null;
  localized_tradition?: string | null;
  rarity: string;
  localized_rarity?: string | null;
  trait: string | null;
  localized_trait?: string | null;
  actions: string | null;
  localized_actions?: string | null;
  trigger: string | null;
  localized_trigger?: string | null;
  range: string | null;
  localized_range?: string | null;
  area: string | null;
  localized_area?: string | null;
  target: string | null;
  localized_target?: string | null;
  duration: string | null;
  localized_duration?: string | null;
  defense: string | null;
  localized_defense?: string | null;
  description: string;
  localized_description?: string | null;
  source_book: string | null;
  source_pg: string | null;
  heighten: string | null;
  localized_heighten?: string | null;
  source: 'wizard' | 'level_planner' | 'manual' | 'innate';
  acquired_at_level: number;
  // 45-spellbook-rework: единая модель записи
  category?: SpellCategory | null;
  source_label?: string | null;     // 'Heritage' | 'Divine Font' | 'Devotion' | 'Subclass'
  frequency?: string | null;        // 'at-will' | 'once-per-day' (innate)
  bonus?: boolean;                  // не тратит выбор игрока (signature/font/innate)
  font_type?: string | null;        // 'heal' | 'harm'
}

export type SpellCategory = 'cantrip' | 'spell' | 'signature' | 'focus' | 'innate' | 'font';

export interface SpellbookResponse {
  character_id: string;
  class_name: string;
  spellcasting_type: string | null;
  tradition: string | null;
  spellcasting_ability: string | null;
  slots: Record<number, number>;
  max_rank: number;
  spells: SpellbookEntry[];
  cantrips: SpellbookEntry[];
  focus_spells: SpellbookEntry[];
  focus_points: number;
  innate_spells: SpellbookEntry[];
  font_spells: SpellbookEntry[];
}

export interface Character {
  id: string;
  user_id: string;
  display?: {
    ancestry?: string | null;
    background?: string | null;
    class_name?: string | null;
    deity?: string | null;
    deity_edicts?: string | null;
    deity_anathema?: string | null;
    deity_description?: string | null;
    [key: string]: unknown;
  };
  resolved_sheet?: Record<string, unknown> | null;
  play_state?: CharacterPlayState | null;
  condition_assessments?: RuleConditionAssessmentResponse | null;
  // Основные
  name: string;
  first_name: string | null;
  last_name: string | null;
  player_name?: string | null;
  level: number;
  ancestry: string;
  background: string;
  class_name: string;
  // Шаг 1 - Анкетные данные
  portrait: string | null;
  age: string | null;
  gender: string | null;
  height: string | null;
  weight: string | null;
  likes: string | null;
  dislikes: string | null;
  catch_phrases: string | null;
  // Происхождение (Step 3)
  heritage: any | null;
  ancestry_feat: any | null;
  alternate_boosts: boolean;
  // Предыстория - бусты (Step 4)
  background_fixed_boost: string | null;
  background_free_boost: string | null;
  // Класс
  class_key_ability: string | null;
  class_boost: string | null;
  class_hp_per_level: number | null;
  class_attack_proficiency: string | null;
  class_armor_proficiency: string | null;
  class_saves_fortitude: string | null;
  class_saves_reflex: string | null;
  class_saves_will: string | null;
  perception_proficiency: string | null;
  // Заклинатель
  is_spellcaster: boolean;
  spell_tradition: string | null;
  spell_attack_proficiency: string | null;
  spell_dc_proficiency: string | null;
  spellcasting_type: string | null;
  spellcasting_ability: string | null;
  // Ability Modifiers
  favorite_attribute?: string;
  ability_str: number;
  ability_dex: number;
  ability_con: number;
  ability_int: number;
  ability_wis: number;
  ability_cha: number;
  // Добровольные штрафы (Step 6)
  voluntary_flaws: any | null;
  // Детали класса (Step 7)
  additional_hp: number;
  trained_proficiencies: string[] | null;
  // Hit Points
  hp_max: number;
  hp_current: number;
  hp_temp: number;
  xp: number;
  hero_points: number;
  dying: number;
  wounded: number;
  // Armor Class
  ac_total: number;
  ac_armor: number;
  ac_dex_cap: number;
  ac_shield: number;
  ac_other: number;
  // Saving Throws
  save_fort_value: number;
  save_fort_ability: number;
  save_fort_proficiency: number;
  save_fort_other: number;
  save_reflex_value: number;
  save_reflex_ability: number;
  save_reflex_proficiency: number;
  save_reflex_other: number;
  save_will_value: number;
  save_will_ability: number;
  save_will_proficiency: number;
  save_will_other: number;
  // JSON fields
  skills: any[];
  feats: any[];
  spells: SpellbookEntry[];
  equipment: any;
  attacks: any[];
  tactics?: PF2eCommanderTactic[] | null;
  // Снаряжение (Step 8)
  starting_gold: number;
  // Финальные детали (Step 10)
  deity_id: string | null;
  deity: string | null;
  deity_source: string | null;
  ancestry_edicts: string | null;
  ancestry_anathema: string | null;
  deity_edicts: string | null;
  deity_anathema: string | null;
  deity_edicts_custom: boolean;
  edicts: string | null;
  anathema: string | null;
  // Скорость передвижения (вычисляется из ancestry, nullable)
  base_speed?: string | null;
  swim_speed?: string | null;
  climb_speed?: string | null;
  fly_speed?: string | null;
  // Level-Up Planner preferences
  level_plan_mode: 'full' | 'gradual' | null;
  level_plan_target_level: number | null;
  // Timestamps
  created_at: string;
  updated_at: string;
  // CharacterDraft v4 (JSONB) — null для старых персонажей без draft
  draft?: Record<string, unknown> | null;
}

export interface CharacterListResponse {
  total: number;
  characters: Character[];
}

export interface CharacterPlayState {
  hp_current: number;
  hp_temp: number;
  xp: number;
  hero_points: number;
  dying: number;
  wounded: number;
  spell_slots_used: Record<string, number>;
  prepared_spells_used: Record<string, boolean>;
  class_daily_choices: ClassDailyChoices;
}

export interface CommanderTacticsDailyChoices {
  kind: 'commander_tactics';
  prepared_tactic_slugs: string[];
}

export interface AnimistApparitionsDailyChoices {
  kind: 'animist_apparitions';
  attuned_apparition_ids: string[];
  primary_apparition_ids: string[];
}

export type ClassDailyChoices =
  | CommanderTacticsDailyChoices
  | AnimistApparitionsDailyChoices
  | Record<string, unknown>;

export type CharacterPlayStatePatch = Partial<CharacterPlayState>;

export interface CharacterNamePatch {
  first_name?: string | null;
  last_name?: string | null;
}

export interface SpellbookSpellInput {
  spell_id: string;
  spell_rank: number;
  tradition: string | null;
}

export type RuleEntityKind =
  | 'ancestry'
  | 'heritage'
  | 'versatile_heritage'
  | 'background'
  | 'class'
  | 'class_feature'
  | 'ancestry_feat'
  | 'feat'
  | 'class_feat'
  | 'spell'
  | 'equipment'
  | 'deity';

export interface RuleEntityRef {
  entity_kind: RuleEntityKind;
  entity_id: string;
  acquisition_key: string;
}

export interface RuleConditionConfirmationInput {
  condition_id: string;
  condition_revision_hash: string;
  acquisition_key: string;
}

export type AccessAcknowledgementBasis =
  | 'access_criterion_attested'
  | 'gm_permission_attested';

export interface AccessAcknowledgementInput {
  reference_entity_id: string;
  acquisition_key: string;
  access_policy_revision_hash: string;
  basis: AccessAcknowledgementBasis;
}

export type RuleConditionAssessmentStatus =
  | 'met'
  | 'unmet'
  | 'manual_confirmation'
  | 'met_now'
  | 'unmet_now'
  | 'manual_at_use'
  | 'manual_access'
  | 'source_error';

export interface RuleConditionReason {
  code: string;
  parameters: Record<string, string | number | boolean>;
}

export interface RuleConditionAssessment {
  condition_id: string | null;
  condition_kind: 'prerequisite' | 'usage_requirement' | 'access';
  clause_key: string | null;
  clause_order: number;
  condition_revision_hash: string | null;
  status: RuleConditionAssessmentStatus;
  source_text: string | null;
  localized_source_text: string | null;
  reasons: RuleConditionReason[];
}

export interface RuleEntityConditionAssessment {
  option: RuleEntityRef;
  reference_entity_id: string | null;
  eligibility_status: 'met' | 'unmet' | 'manual_confirmation' | 'source_error';
  prerequisites: RuleConditionAssessment[];
  requirements: RuleConditionAssessment[];
  access: {
    rarity: 'common' | 'uncommon' | 'rare' | 'unique';
    policy_status: 'granted' | 'criteria_confirmation_required' | 'gm_permission_required' | 'source_error';
    policy_revision_hash: string;
    required_basis: AccessAcknowledgementBasis | null;
    conditions: RuleConditionAssessment[];
    reasons: RuleConditionReason[];
  };
  choice_requirements: RuleChoiceRequirement[];
  choice_supported: boolean;
  choice_unavailable_reason: string | null;
}

export interface RuleConditionAssessmentResponse {
  schema_version: 1;
  eligibility_context_fingerprint: string;
  usage_context_fingerprint: string | null;
  usage_context_fresh: boolean;
  assessments: RuleEntityConditionAssessment[];
}

export type CharacterPreviewResponse = Record<string, unknown> & {
  condition_assessments: RuleConditionAssessmentResponse;
};

export type CreateCharacterDto = Omit<Character, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'level_plan_mode' | 'level_plan_target_level' | 'spells'> & {
  spellbook_spells?: SpellbookSpellInput[];
  rule_condition_confirmations?: RuleConditionConfirmationInput[];
  access_acknowledgements?: AccessAcknowledgementInput[];
};
export type UpdateCharacterDto = Partial<CreateCharacterDto>;

export function normalizeDisplayLocale(locale?: string | null): string | undefined {
  const normalized = locale?.split('-', 1)[0]?.toLowerCase();
  return normalized && normalized !== 'en' ? normalized : undefined;
}

function localizedRequestConfig(locale?: string | null) {
  const normalized = normalizeDisplayLocale(locale);
  return normalized ? { params: { locale: normalized } } : undefined;
}

export const characterApi = {
  getAll: (locale?: string) =>
    api.get<CharacterListResponse>('/characters', localizedRequestConfig(locale)),
  getAllLocalized: (locale: string) =>
    api.get<CharacterListResponse>('/characters', localizedRequestConfig(locale)),
  getById: (id: string, locale?: string) =>
    api.get<Character>(`/characters/${id}`, localizedRequestConfig(locale)),
  getByIdLocalized: (id: string, locale: string) =>
    api.get<Character>(`/characters/${id}`, localizedRequestConfig(locale)),
  create: (data: CreateCharacterDto) => api.post<Character>('/characters', data),
  createLocalized: (data: CreateCharacterDto, locale: string) =>
    api.post<Character>('/characters', data, localizedRequestConfig(locale)),
  update: (id: string, data: UpdateCharacterDto) =>
    api.put<Character>(`/characters/${id}`, data),
  updateLocalized: (id: string, data: UpdateCharacterDto, locale: string) =>
    api.put<Character>(`/characters/${id}`, data, localizedRequestConfig(locale)),
  saveInventoryPlacement: (id: string, equipment: unknown, locale?: string) =>
    api.patch<Character>(
      `/characters/${id}/inventory/placement`,
      { equipment },
      localizedRequestConfig(locale),
    ),
  acquireCatalogItem: (id: string, equipmentId: string, acquisitionMode: 'free' | 'paid', locale?: string) =>
    api.post<Character>(
      `/characters/${id}/inventory/catalog/${equipmentId}`,
      { acquisition_mode: acquisitionMode },
      localizedRequestConfig(locale),
    ),
  copyCustomInventoryInstance: (
    id: string,
    instanceId: string,
    expectedLibraryRevision: number | null,
    locale?: string,
  ) => api.post<Character>(
    `/characters/${id}/inventory/instances/${instanceId}/copy`,
    { expected_library_revision: expectedLibraryRevision },
    localizedRequestConfig(locale),
  ),
  updateInventoryRunes: (id: string, instanceId: string, runes: unknown, locale?: string) =>
    api.patch<Character>(
      `/characters/${id}/inventory/instances/${instanceId}/runes`,
      { runes },
      localizedRequestConfig(locale),
    ),
  savePlayState: (id: string, data: CharacterPlayStatePatch, locale?: string) =>
    api.patch<Character>(`/characters/${id}/play-state`, data, localizedRequestConfig(locale)),
  saveName: (id: string, data: CharacterNamePatch, locale?: string) =>
    api.patch<Character>(`/characters/${id}/name`, data, localizedRequestConfig(locale)),
  savePlanPrefs: (id: string, mode: 'full' | 'gradual', targetLevel: number) =>
    api.patch<Character>(`/characters/${id}/plan-prefs`, {
      level_plan_mode: mode,
      level_plan_target_level: targetLevel,
    }),
  delete: (id: string) => api.delete(`/characters/${id}`),
  preview: (draft: Record<string, unknown>, level: number, locale?: string) =>
    api.post<CharacterPreviewResponse>('/characters/preview', { draft, level, locale }),
  assessConditions: (
    draft: Record<string, unknown>,
    level: number,
    options: RuleEntityRef[],
    locale?: string,
  ) => api.post<RuleConditionAssessmentResponse>(
    '/characters/condition-assessments',
    { draft, level, options, locale },
  ),
  applyClassPackage: (draft: Record<string, unknown>, level: number, locale?: string) =>
    api.post<{ equipment: Record<string, unknown>; warnings: Record<string, unknown>[]; added_count: number }>(
      '/characters/wizard-equipment/class-package',
      { draft, level, locale },
    ),
  getSpellbook: (id: string, locale?: string) => api.get<SpellbookResponse>(`/characters/${id}/spellbook`, localizedRequestConfig(locale)),
  updateSpellbook: (id: string, data: { add_spell_ids?: string[]; remove_spell_ids?: string[] }, locale?: string) =>
    api.patch<SpellbookResponse>(`/characters/${id}/spellbook`, data, localizedRequestConfig(locale)),
};

export type CompanionKind = 'animal_companion' | 'familiar' | 'pet' | 'construct_companion';
export type ConstructCompanionStage = 'prototype' | 'advanced' | 'incredible' | 'paragon';
export type CompanionSize = 'tiny' | 'small' | 'medium' | 'large' | 'huge' | 'gargantuan';
export interface AnimalCompanionTemplateChoices { size: CompanionSize; }
export interface ConstructTemplateChoices { size: 'small' | 'medium'; form: 'humanoid' | 'quadruped' | 'wheeled' | 'other'; primary_strike_name: string; secondary_strike_name: string; secondary_damage_type: 'piercing' | 'slashing'; }
export interface ConstructCanonicalChoices { modification_ids: string[]; advanced_size?: 'small' | 'medium' | 'large' | null; projectile_damage_type?: 'bludgeoning' | 'piercing' | null; advanced_configuration_confirmed?: boolean; }
export interface ConstructCompanionModification { id: string; slug: string; name: string; description: string; tier: 'initial'; source_book: string; source_url: string; rules: Record<string, unknown>; }
export interface CompanionEntity { id: string; owner_user_id: string; kind: CompanionKind; name: string; portrait?: string | null; appearance?: string | null; notes?: string | null; animal_type_id?: string | null; specific_familiar_id?: string | null; template_choices?: AnimalCompanionTemplateChoices | PetTemplateChoices | ConstructTemplateChoices | Record<string, never>; template_schema_version: number; template_state: 'ready' | 'needs_chassis' | 'needs_configuration'; baseline_resolved_sheet?: Record<string, unknown> | null; is_archived: boolean; created_at: string; updated_at: string; }
interface CompanionEntityCreateBase { name: string; portrait?: string | null; appearance?: string | null; notes?: string | null; }
export type CompanionEntityCreate =
  | (CompanionEntityCreateBase & { kind: 'animal_companion'; animal_type_id: string; template_choices: AnimalCompanionTemplateChoices })
  | (CompanionEntityCreateBase & { kind: 'familiar'; specific_familiar_id?: string | null; animal_type_id?: never })
  | (CompanionEntityCreateBase & { kind: 'pet'; template_choices: PetTemplateChoices; animal_type_id?: never; specific_familiar_id?: never })
  | (CompanionEntityCreateBase & { kind: 'construct_companion'; template_choices: ConstructTemplateChoices; animal_type_id?: never; specific_familiar_id?: never });
export interface CompanionEntityUpdate { name?: string; portrait?: string | null; appearance?: string | null; notes?: string | null; animal_type_id?: string; specific_familiar_id?: string | null; template_choices?: ConstructTemplateChoices | AnimalCompanionTemplateChoices | PetTemplateChoices; is_archived?: boolean; }
export type AnimalCompanionStage = 'young' | 'mature' | 'nimble' | 'savage' | 'specialized';
export interface CompanionSlot { rule_slot_key: string; allowed_kind: CompanionKind; required: boolean; state: 'bound' | 'missing' | 'optional'; binding_id?: string | null; companion_entity_id?: string | null; reason?: string | null; selector?: string; source_ref: Record<string, unknown>; locks?: Record<string, unknown>; allowed_stages?: Array<AnimalCompanionStage | ConstructCompanionStage>; progression_stage_options?: Array<AnimalCompanionStage | ConstructCompanionStage>; allowed_specialization_slugs?: string[]; current_stage?: AnimalCompanionStage | null; construct_stage?: ConstructCompanionStage | null; specialization_id?: string | null; canonical_choices?: CompanionCanonicalChoices; requires_progression_choice?: boolean; progression_reason?: string | null; fixed_ability_capacity?: number; daily_ability_capacity?: number; specific_familiar_capacity?: number; modification_capacity?: number; conflict_group?: string | null; mount_required?: boolean; }
export type AnimalCompanionFamily = 'ordinary' | 'plant' | 'elemental' | 'undead' | 'unique';
export interface CompanionDefenseEntry { type: string; scaling?: 'level'; value?: number; }
export interface AnimalCompanionType { id: string; slug: string; name: string; localized_name?: string | null; source_key: string; source_book: string; localized_source_book?: string | null; source_page?: string | null; source_url?: string | null; family: AnimalCompanionFamily; traits: string[]; rarity: 'common' | 'uncommon' | 'rare' | 'unique'; minimum_level: number; access: { text?: string }; recovery_profile: 'living' | 'elemental' | 'void'; immunities: string[]; weaknesses: CompanionDefenseEntry[]; resistances: CompanionDefenseEntry[]; variant_of_type_id?: string | null; eligible: boolean; eligibility_reasons: string[]; mount_eligible: boolean; mount_eligibility_reasons: string[]; mount_minimum_level?: number | null; size: string; ancestry_hp: number; attribute_modifiers: Record<string, number>; strikes: Array<Record<string, unknown>>; senses: Array<{ text: string }>; is_mount: boolean; speeds: Array<{ type: string; feet: number }>; }
export interface AnimalCompanionSpecialization { id: string; slug: string; name: string; rule_data: { text?: string }; }
export type CompanionLifecycle = 'active' | 'inactive' | 'dead' | 'destroyed' | 'retired';
export interface FamiliarAbility { id: string; slug: string; name: string; localized_name?: string | null; family: 'familiar' | 'master'; description?: string | null; is_pet_ability: boolean; }
export interface SpecificFamiliarAbility { name: string; slug: string; localized_name?: string | null; }
export interface SpecificFamiliar { id: string; slug: string; name: string; localized_name?: string | null; source_key: string; source_book: string; source_url: string; traits: string[]; rarity: 'common' | 'uncommon' | 'rare' | 'unique'; access?: string | null; required_abilities: number; granted_abilities: SpecificFamiliarAbility[]; unique_abilities: SpecificFamiliarAbility[]; description?: string | null; }
export type PetForm = 'terrestrial' | 'aquatic';
export interface PetCanonicalChoices { form: PetForm; ability_ids: string[]; fast_movement_speed?: 'land' | 'swim' | 'climb' | 'fly' | null; }
export interface PetTemplateChoices extends PetCanonicalChoices { animal_type: string; }
export interface CompanionCanonicalChoices extends Partial<PetCanonicalChoices>, Partial<ConstructCanonicalChoices> { advancement_stage?: 'nimble' | 'savage'; racer_speed?: string; access_confirmed?: boolean; form_ability?: string; [key: string]: unknown; }
export interface CompanionPlayState { hp_current: number; hp_temp: number; dying?: number; wounded?: number; state?: Record<string, unknown>; prepared_at?: string | null; }
export interface CompanionPlayStatePatch { hp_current?: number; hp_temp?: number; dying?: number; wounded?: number; }
export interface CompanionBinding { id: string; character_id: string; companion_entity_id: string; rule_slot_key: string; kind: CompanionKind; lifecycle: CompanionLifecycle; animal_type_id?: string | null; stage?: AnimalCompanionStage | null; specialization_id?: string | null; canonical_choices?: CompanionCanonicalChoices; choices_schema_version: number; source_ref?: Record<string, unknown>; familiar_loadout?: Array<{ ability_id: string }>; resolved_sheet?: Record<string, unknown> | null; play_state?: CompanionPlayState | null; revision: number; }
export interface CompanionBindingRequest { companion_entity_id: string; rule_slot_key: string; animal_type_id?: string | null; stage?: AnimalCompanionStage | null; specialization_id?: string | null; canonical_choices?: CompanionCanonicalChoices; choices_schema_version?: number; lifecycle?: CompanionLifecycle; pet_replacement_downtime_completed?: boolean; }
export interface CompanionBindingDetachResult { binding_id: string; archived: boolean; archive_reason: string; }

export const companionApi = {
  list: (locale?: string) => api.get<CompanionEntity[]>('/companions', { params: locale ? { locale } : undefined }),
  create: (data: CompanionEntityCreate) => api.post<CompanionEntity>('/companions', data),
  update: (id: string, data: CompanionEntityUpdate) => api.patch<CompanionEntity>(`/companions/${id}`, data),
  previewTemplate: (data: { kind: CompanionKind; name: string; animal_type_id?: string | null; specific_familiar_id?: string | null; template_choices?: ConstructTemplateChoices | AnimalCompanionTemplateChoices | PetTemplateChoices }, locale?: string) => api.post<Record<string, unknown>>('/companions/preview', data, { params: locale ? { locale } : undefined }),
  remove: (id: string, confirmDetach = false) => api.delete(`/companions/${id}`, { params: { confirm_detach: confirmDetach } }),
  eligibility: (characterId: string) => api.get<{ slots: CompanionSlot[]; gaps: Array<Record<string, unknown>> }>(`/characters/${characterId}/companions/eligibility`),
  listBindings: (characterId: string) => api.get<CompanionBinding[]>(`/characters/${characterId}/companions`),
  attach: (characterId: string, data: CompanionBindingRequest) => api.post<CompanionBinding>(`/characters/${characterId}/companions`, data),
  replace: (characterId: string, ruleSlotKey: string, data: CompanionBindingRequest) => api.put<CompanionBinding>(`/characters/${characterId}/companions/${encodeURIComponent(ruleSlotKey)}`, data),
  detachBinding: (bindingId: string) => api.delete<CompanionBindingDetachResult>(`/companions/bindings/${bindingId}`),
  animalTypes: (characterId?: string, level?: number, locale?: string) => api.get<AnimalCompanionType[]>('/companion-catalogues/animal-types', { params: { ...(characterId ? { character_id: characterId } : {}), ...(level ? { level } : {}), ...(locale ? { locale } : {}) } }),
  animalSpecializations: () => api.get<AnimalCompanionSpecialization[]>('/companion-catalogues/animal-specializations'),
  familiarAbilities: (locale?: string) => api.get<FamiliarAbility[]>('/companion-catalogues/familiar-abilities', { params: locale ? { locale } : undefined }),
  specificFamiliars: (capacity?: number, locale?: string) => api.get<SpecificFamiliar[]>('/companion-catalogues/specific-familiars', { params: { ...(typeof capacity === 'number' ? { capacity } : {}), ...(locale ? { locale } : {}) } }),
  petAbilities: (locale?: string) => api.get<FamiliarAbility[]>('/companion-catalogues/pet-abilities', { params: locale ? { locale } : undefined }),
  constructModifications: () => api.get<ConstructCompanionModification[]>('/companion-catalogues/construct-modifications'),
  resolvedSheet: (bindingId: string, locale?: string) => api.get<{ binding_id: string; binding: CompanionBinding; resolved_sheet: Record<string, unknown> | null; play_state: CompanionPlayState | null }>(`/companions/bindings/${bindingId}/resolved-sheet`, { params: locale ? { locale } : undefined }),
  setPlayState: (bindingId: string, data: CompanionPlayStatePatch) => api.patch<CompanionPlayState>(`/companions/bindings/${bindingId}/play-state`, data),
  setLifecycle: (bindingId: string, lifecycle: CompanionLifecycle, revision: number) => api.patch<CompanionBinding>(`/companions/bindings/${bindingId}/lifecycle`, { lifecycle, revision }),
  prepareFamiliar: (bindingId: string, familiar_loadout: Array<{ ability_id: string }>, revision: number) => api.patch<CompanionBinding>(`/companions/bindings/${bindingId}/familiar-loadout`, { familiar_loadout, revision }),
  setConstructState: (bindingId: string, action: 'mark_broken' | 'repair' | 'destroy' | 'rebuild', revision: number) => api.patch<CompanionBinding>(`/companions/bindings/${bindingId}/construct-state`, { action, revision }),
};

// ==================== User Panel API ====================

export const userPanelApi = {
  updatePreferences: (data: { show_english_names?: boolean; is_game_master?: boolean }) =>
    api.patch('/user/preferences', data),
  changePassword: (data: {
    current_password: string;
    new_password: string;
    confirm_password: string;
  }) => api.post('/user/change-password', data),
  setup2FA: () => api.get<TwoFactorSetupResponse>('/user/2fa/setup'),
  enable2FA: (twoFactorCode: string) =>
    api.post('/user/2fa/enable', undefined, { params: { two_factor_code: twoFactorCode } }),
  disable2FA: (data: { two_factor_code: string }) => api.post('/user/2fa/disable', data),
  deleteAccount: (data: { password: string }) => api.delete('/user/delete-account', { data }),
};

// ==================== Health API ====================

export interface HealthResponse {
  status: string;
  version?: string;
}

export const healthApi = {
  check: () => api.get<HealthResponse>('/health'),
};

// ==================== Admin API ====================

export interface ServerStatsResponse {
  cpu: {
    percent: number;
    cores: number;
    freq_current: number | null;
    freq_min: number | null;
    freq_max: number | null;
  };
  memory: {
    total: number;
    available: number;
    used: number;
    percent: number;
  };
  disk: {
    total: number;
    used: number;
    free: number;
    percent: number;
  };
  containers: Array<{
    name: string;
    status: string;
    health: string;
    uptime: string | null;
    restart_count: number;
  }>;
  timestamp: string;
}

export interface MetricDataPoint {
  timestamp: string;
  cpu_percent: number;
  memory_percent: number;
  disk_percent: number;
}

export interface MetricsHistoryResponse {
  data_points: MetricDataPoint[];
  minutes: number;
}

export interface UserAdminResponse {
  id: string;
  email: string;
  is_active: boolean;
  is_verified: boolean;
  is_admin: boolean;
  is_game_master: boolean;
  is_localizator: boolean;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
  must_change_password: boolean;
  two_factor_enabled: boolean;
  two_factor_configured: boolean;
  must_setup_two_factor: boolean;
  locked_until: string | null;
  characters_count: number;
  creatures_count: number;
  custom_items_count: number;
}

export interface UserListResponse {
  total: number;
  users: UserAdminResponse[];
  page: number;
  page_size: number;
  total_pages: number;
}

export interface UserCreateRequest {
  email: string;
  password: string;
  is_admin: boolean;
  is_game_master: boolean;
  is_localizator: boolean;
  is_active: boolean;
}

export interface UserUpdateRequest {
  email?: string;
  password?: string;
  is_admin?: boolean;
  is_game_master?: boolean;
  is_localizator?: boolean;
  is_active?: boolean;
  must_change_password?: boolean;
}

export interface AdminActionResponse {
  success: boolean;
  message: string;
  user_id?: string;
}

export interface PlatformStatsResponse {
  total_users: number;
  active_users: number;
  admin_users: number;
  total_characters: number;
  registration_enabled: boolean;
}

export interface ContainerHealth {
  name: string;
  status: string;
  health: string;
  uptime: string | null;
  restart_count: number;
}

export interface RegistrationSettingsResponse {
  is_enabled: boolean;
  changed_by_email: string | null;
  changed_at: string | null;
  comment: string | null;
}

export interface RegistrationSettingsUpdateRequest {
  is_enabled: boolean;
  comment: string;
}

export interface LogSettingsResponse {
  log_level_global: string;
  client_log_enabled: boolean;
  client_log_sampling_rate: number;
}

export interface LogSettingsUpdateRequest {
  log_level_global?: string;
  client_log_enabled?: boolean;
  client_log_sampling_rate?: number;
}

export interface BackupFileInfo {
  filename: string;
  type: 'manual' | 'scheduled';
  path: string;
  size_bytes: number;
  size_human: string;
  created_at: string;
}

export interface BackupListResponse {
  backups: { manual: BackupFileInfo[] };
  manual_backups: BackupFileInfo[];
  scheduled_backups: Record<string, BackupFileInfo[]>;
  total_size_bytes: number;
  total_size_human: string;
  backup_enabled: boolean;
}

export interface BackupScheduleEntry {
  enabled: boolean;
  time: string;
  days_of_week?: number[];
  day_of_week?: number;
  day_of_month?: number;
  next_run?: string | null; // C18: read-only, источник — host-cron
}

export interface BackupScheduleJob {
  id: string;
  kind: 'daily' | 'monthly';
  name: string;
  folder: string;
  path?: string;
  retention: number;
  enabled: boolean;
  time: string;
  days_of_week?: number[];
  day_of_month?: number;
  next_run?: string | null;
  marker?: string | null;
}

export interface BackupConfig {
  enabled: boolean; // C18: read-only (производное от cron)
  path: string;
  // C18: расписание — read-only, источник host-cron /etc/cron.d/pf2e
  schedule: {
    daily: BackupScheduleEntry;
    weekly: BackupScheduleEntry;
    monthly: BackupScheduleEntry;
  };
  schedule_jobs: BackupScheduleJob[];
  schedule_source?: string;
  schedule_timezone?: string;
  retention: { manual: number };
  verify: { enabled: boolean; notify_on_failure: boolean };
  offsite: { enabled: boolean; type: string; target: string };
}

export interface BackupVerifyResult {
  status?: 'passed' | 'failed' | 'error' | string;
  message?: string;
  timestamp?: string;
}

export interface BackupJobStatus {
  job_id?: string;
  type?: 'manual';
  status: 'idle' | 'queued' | 'running' | 'completed' | 'failed' | string;
  filename?: string | null;
  started_at?: string | null;
  updated_at?: string | null;
  completed_at?: string | null;
  elapsed_seconds?: number;
  current_size_bytes?: number;
  current_size_human?: string;
  error?: string | null;
  backup?: BackupFileInfo | null;
}

// ─── Logs (C17) ──────────────────────────────────────────────────────────────
export interface LogFileInfo {
  key: string;
  path: string;
  size_bytes: number;
  mtime: number;
  category: string;
  namespace: string;
  rotated_count: number;
  rotated_bytes: number;
}

export interface LogDiskUsage {
  total_bytes: number;
  by_category: Record<string, number>;
  disk_free_bytes: number;
  disk_total_bytes: number;
}

export interface LogTailResponse {
  lines: string[];
  returned: number;
  capped: boolean;
}

export interface LogRotationInfo {
  python: {
    when: string;
    backup_count: number;
    encoding: string;
    logs: Array<{ key: string; namespace: string; category: string; rotation_owner: string }>;
  };
  note: string;
}

export interface LogAlertBucket {
  bucket: string;
  WARN: number;
  ERROR: number;
}

export interface LogAlertsResponse {
  period: string;
  bucket: string;
  series: LogAlertBucket[];
}

export const adminApi = {
  // Полная статистика (CPU + Memory + Disk + Containers)
  getServerStats: () => api.get<ServerStatsResponse>('/admin/stats'),

  // Быстрая статистика БЕЗ контейнеров
  getBasicStats: () => api.get<ServerStatsResponse>('/admin/stats/basic'),

  // Только контейнеры (медленно)
  getContainerStats: () => api.get<ContainerHealth[]>('/admin/stats/containers'),

  // Статистика платформы (users, chars, registration)
  getPlatformStats: () => api.get<PlatformStatsResponse>('/admin/platform-stats'),

  // Последние строки лог-файла (tail с конца)
  getLogs: (file: string, lines = 100, level?: string) =>
    api.get<LogTailResponse>('/admin/logs', { params: { file, lines, level } }),

  // Динамический список лог-файлов
  getLogFiles: () => api.get<{ files: LogFileInfo[] }>('/admin/logs/files'),

  // Использование диска логами + свободно
  getLogDisk: () => api.get<LogDiskUsage>('/admin/logs/disk'),

  // Очистить (truncate) лог-файл
  clearLog: (file: string) => api.post<{ success: boolean; file: string }>('/admin/logs/clear', { file }),

  // Read-only настройки ротации
  getLogRotation: () => api.get<LogRotationInfo>('/admin/logs/rotation'),

  // График алертов (WARN/ERROR по корзинам)
  getLogAlerts: (period: '24h' | '7d' | '30d' = '24h', bucket: 'hour' | 'day' = 'hour') =>
    api.get<LogAlertsResponse>('/admin/logs/alerts', { params: { period, bucket } }),

  // Metrics history
  getMetricsHistory: (minutes: number = 30) =>
    api.get<MetricsHistoryResponse>('/admin/metrics', { params: { minutes } }),

  // Users
  getUsers: (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    is_active?: boolean;
    is_admin?: boolean;
  }) => api.get<UserListResponse>('/admin/users', { params }),

  getUserById: (userId: string) => api.get<UserAdminResponse>(`/admin/users/${userId}`),

  createUser: (data: UserCreateRequest) =>
    api.post<AdminActionResponse>('/admin/users', data),

  updateUser: (userId: string, data: UserUpdateRequest) =>
    api.put<AdminActionResponse>(`/admin/users/${userId}`, data),

  deleteUser: (userId: string) =>
    api.delete<AdminActionResponse>(`/admin/users/${userId}`),

  deactivateUser: (userId: string) =>
    api.post<AdminActionResponse>(`/admin/users/${userId}/deactivate`),

  activateUser: (userId: string) =>
    api.post<AdminActionResponse>(`/admin/users/${userId}/activate`),

  toggleUserTwoFactor: (userId: string) =>
    api.post<AdminActionResponse>(`/admin/users/${userId}/toggle-2fa`),

  toggleAdminPrivileges: (userId: string) =>
    api.post<AdminActionResponse>(`/admin/users/${userId}/toggle-admin`),

  toggleLocalizator: (userId: string) =>
    api.post<AdminActionResponse>(`/admin/users/${userId}/toggle-localizator`),

  // Registration Settings
  getRegistrationSettings: () => api.get<RegistrationSettingsResponse>('/admin/registration-settings'),

  updateRegistrationSettings: (data: RegistrationSettingsUpdateRequest) =>
    api.put<RegistrationSettingsResponse>('/admin/registration-settings', data),

  // Log Settings
  getLogSettings: () => api.get<LogSettingsResponse>('/admin/log-settings'),

  updateLogSettings: (data: LogSettingsUpdateRequest) =>
    api.patch<LogSettingsResponse>('/admin/log-settings', data),

  // Backup
  getBackups: () => api.get<BackupListResponse>('/admin/backups'),
  runBackup: (type: 'manual') =>
    api.post<BackupJobStatus>('/admin/backups/run', { type }),
  getCurrentBackupJob: () => api.get<BackupJobStatus>('/admin/backups/run/current'),
  getBackupJob: (jobId: string) => api.get<BackupJobStatus>(`/admin/backups/run/${jobId}`),
  restoreBackup: (filename: string, confirmToken: string) =>
    api.post<{ status: string; filename: string }>('/admin/backups/restore', {
      filename,
      confirm_token: confirmToken,
    }),
  deleteBackup: (filename: string) =>
    api.post<{ status: string; filename: string }>('/admin/backups/delete', { filename }),
  getBackupConfig: () => api.get<BackupConfig>('/admin/backups/config'),
  updateBackupConfig: (config: Partial<BackupConfig>) =>
    api.put<BackupConfig>('/admin/backups/config', { config }),
  getBackupVerify: () => api.get<BackupVerifyResult>('/admin/backups/verify'),
  runBackupVerify: () => api.post<{ status: string }>('/admin/backups/verify/run', {}),
};

// ==================== Public Registration Status API ====================

export interface RegistrationStatusResponse {
  is_enabled: boolean;
}

export const publicAuthApi = {
  getRegistrationStatus: () => api.get<RegistrationStatusResponse>('/auth/registration-status'),
};

// ==================== PF2e Reference API (Public) ====================

export interface PF2eAncestry {
  id: string;
  name: string;
  localized_name?: string | null;
  localized_description?: string | null;
  localized_physical_description?: string | null;
  localized_names?: string | null;
  localized_special_abilities1_full?: string | null;
  localized_special_abilities2_full?: string | null;
  localized_size?: string | null;
  localized_speed?: string | null;
  localized_ability_boost?: string | null;
  localized_ability_flaw?: string | null;
  localized_language?: string | null;
  localized_vision?: string | null;
  localized_trait?: string | null;
  localized_rarity?: string | null;
  localized_edicts?: string[] | null;
  localized_anathema?: string[] | null;
  hp: string;
  size: string;
  speed: string;
  ability_boost: string;
  ability_flaw: string | null;
  language: string;
  vision: string;
  trait: string;
  rarity: string;
  description: string | null;
  edicts: string[];
  anathema: string[];
  license: string | null;
  source_book: string;
  source_pg: number | null;
  physical_description: string | null;
  names: string | null;
  special_abilities1: string | null;
  special_abilities1_full: string | null;
  special_abilities2: string | null;
  special_abilities2_full: string | null;
  created_at: string;
  updated_at: string;
}

export interface PF2eAncestryListResponse {
  total: number;
  ancestries: PF2eAncestry[];
}

export interface PF2eHeritage {
  id: string;
  ancestry_id: string;
  name: string;
  localized_name?: string | null;
  traits: string | null;
  description: string;
  localized_description?: string | null;
  source_book: string | null;
  source_pg: number | null;
  benefit_vision: string | null;
  localized_benefit_vision?: string | null;
  benefit_speed_land: string | null;
  localized_benefit_speed_land?: string | null;
  benefit_speed_swim: string | null;
  localized_benefit_speed_swim?: string | null;
  benefit_speed_climb: string | null;
  localized_benefit_speed_climb?: string | null;
  benefit_hp: string | null;
  localized_benefit_hp?: string | null;
  benefit_size: string | null;
  localized_benefit_size?: string | null;
  benefit_trained: string | null;
  localized_benefit_trained?: string | null;
  benefit_feat: string | null;
  localized_benefit_feat?: string | null;
  benefit_action: string | null;
  localized_benefit_action?: string | null;
  benefit_spellcasting: string | null;
  localized_benefit_spellcasting?: string | null;
  benefit_resistance: string | null;
  localized_benefit_resistance?: string | null;
  benefit_immunity: string | null;
  localized_benefit_immunity?: string | null;
  benefit_sense: string | null;
  localized_benefit_sense?: string | null;
  benefit_traits: string | null;
  localized_benefit_traits?: string | null;
  benefit_languages: string | null;
  localized_benefit_languages?: string | null;
  benefit_unarmed_attack: string | null;
  localized_benefit_unarmed_attack?: string | null;
  benefit_armor: string | null;
  localized_benefit_armor?: string | null;
  benefit_light: string | null;
  localized_benefit_light?: string | null;
  benefit_save_bonus: string | null;
  localized_benefit_save_bonus?: string | null;
  benefit_healing: string | null;
  localized_benefit_healing?: string | null;
  license: string | null;
  created_at: string;
  updated_at: string;
}

export interface PF2eHeritageListResponse {
  total: number;
  heritages: PF2eHeritage[];
}

export interface PF2eVersatileHeritage {
  id: string;
  name: string;
  localized_name?: string | null;
  traits: string | null;
  description: string;
  localized_description?: string | null;
  source_book: string | null;
  source_pg: number | null;
  benefit_vision: string | null;
  localized_benefit_vision?: string | null;
  benefit_speed_land: string | null;
  localized_benefit_speed_land?: string | null;
  benefit_speed_swim: string | null;
  localized_benefit_speed_swim?: string | null;
  benefit_speed_climb: string | null;
  localized_benefit_speed_climb?: string | null;
  benefit_hp: string | null;
  localized_benefit_hp?: string | null;
  benefit_size: string | null;
  localized_benefit_size?: string | null;
  benefit_trained: string | null;
  localized_benefit_trained?: string | null;
  benefit_feat: string | null;
  localized_benefit_feat?: string | null;
  benefit_action: string | null;
  localized_benefit_action?: string | null;
  benefit_spellcasting: string | null;
  localized_benefit_spellcasting?: string | null;
  benefit_resistance: string | null;
  localized_benefit_resistance?: string | null;
  benefit_immunity: string | null;
  localized_benefit_immunity?: string | null;
  benefit_sense: string | null;
  localized_benefit_sense?: string | null;
  benefit_traits: string | null;
  localized_benefit_traits?: string | null;
  benefit_languages: string | null;
  localized_benefit_languages?: string | null;
  benefit_unarmed_attack: string | null;
  localized_benefit_unarmed_attack?: string | null;
  benefit_armor: string | null;
  localized_benefit_armor?: string | null;
  benefit_light: string | null;
  localized_benefit_light?: string | null;
  benefit_save_bonus: string | null;
  localized_benefit_save_bonus?: string | null;
  benefit_healing: string | null;
  localized_benefit_healing?: string | null;
  license: string | null;
  created_at: string;
  updated_at: string;
}

export interface PF2eVersatileHeritageListResponse {
  total: number;
  heritages: PF2eVersatileHeritage[];
}

export interface FeatChoiceRequirement {
  key: string;
  choice_type: 'skill';
  count: number;
  distinct: boolean;
  current_rank: 'Untrained' | null;
  result_rank: 'Trained' | null;
}

export interface PF2eAncestryFeat {
  id: string;
  ancestry_name: string;
  localized_ancestry_name?: string | null;
  name: string;
  localized_name?: string | null;
  trait: string | null;
  rarity: string;
  level: number;
  benefit_feat: string | null;
  localized_benefit_feat?: string | null;
  benefit_spell: string | null;
  benefit_action: string | null;
  description: string | null;
  localized_description?: string | null;
  prerequisite_name: string | null;
  localized_prerequisite_name?: string | null;
  prerequisite: string | null;
  localized_prerequisite?: string | null;
  access: string | null;
  localized_access?: string | null;
  requirements: string | null;
  localized_requirements?: string | null;
  frequency: string | null;
  localized_frequency?: string | null;
  trigger: string | null;
  localized_trigger?: string | null;
  critical_success: string | null;
  localized_critical_success?: string | null;
  success: string | null;
  localized_success?: string | null;
  failure: string | null;
  localized_failure?: string | null;
  critical_failure: string | null;
  localized_critical_failure?: string | null;
  enhancement: string | null;
  localized_enhancement?: string | null;
  special: string | null;
  localized_special?: string | null;
  license: string | null;
  source_book: string | null;
  source_pg: number | null;
  slug?: string | null;
  foundry_id?: string | null;
  max_takable: number | null;
  choice_requirements: FeatChoiceRequirement[];
  created_at: string;
  updated_at: string;
}

export interface PF2eAncestryFeatListResponse {
  total: number;
  feats: PF2eAncestryFeat[];
}

// ==================== Backgrounds (Step 4) ====================

export interface PF2eBackground {
  id: string;
  name: string;
  localized_name?: string | null;
  localized_description?: string | null;
  localized_ability?: string | null;
  localized_skill?: string | null;
  localized_skill_lore?: string | null;
  localized_special_grant?: string | null;
  localized_feat?: string | null;
  ability: string | null;
  skill: string | null;
  skill_lore: string | null;
  special_grant: string | null;
  feat: string | null;
  rarity: string;
  description: string | null;
  license: string | null;
  source_book: string | null;
  source_pg: number | null;
  created_at: string;
  updated_at: string;
}

export interface PF2eBackgroundListResponse {
  total: number;
  backgrounds: PF2eBackground[];
}

// ==================== Classes (Step 5) ====================

export interface PF2eClass {
  id: string;
  name: string;
  localized_name?: string | null;
  localized_description?: string | null;
  localized_ability?: string | null;
  localized_tradition?: string | null;
  localized_attack_proficiency?: string | null;
  localized_defense_proficiency?: string | null;
  localized_fortitude_proficiency?: string | null;
  localized_reflex_proficiency?: string | null;
  localized_will_proficiency?: string | null;
  localized_perception_proficiency?: string | null;
  localized_perception_details?: string | null;
  localized_skill_proficiency?: string | null;
  localized_spellcasting_type?: string | null;
  localized_spellcasting_ability?: string | null;
  localized_key_features?: string | null;
  localized_class_dc_progression?: string | null;
  localized_attack_progression?: string | null;
  localized_saving_throw_progression?: string | null;
  localized_key_terms?: string | null;
  localized_rarity?: string | null;
  localized_trained_skills?: string | null;
  localized_key_ability?: string | null;
  trait: string | null;
  ability: string | null;
  hp: number;
  tradition: string | null;
  attack_proficiency: string | null;
  defense_proficiency: string | null;
  fortitude_proficiency: string | null;
  reflex_proficiency: string | null;
  will_proficiency: string | null;
  perception_proficiency: string | null;
  perception_details: string | null;
  skill_proficiency: string | null;
  speed: number | null;
  spellcasting_type: string | null;
  spellcasting_ability: string | null;
  key_features: string | null;
  class_dc_progression: string | null;
  attack_progression: string | null;
  saving_throw_progression: string | null;
  description: string | null;
  key_terms: string | null;
  rarity: string;
  class_feat_levels: string | null;
  skill_feat_levels: string | null;
  general_feat_levels: string | null;
  ancestry_feat_levels: string | null;
  skill_increase_levels: string | null;
  trained_skills: string | null;
  trained_skills_additional: number | null;
  trained_skill_choices: string[][];
  key_ability: string | null;
  has_spellcasting: boolean | null;
  spell_selection_plan?: {
    spellcastingType: string;
    traditionOptions: string[];
    defaultTradition: string;
    spellcastingAbility: string | null;
    groups: Array<{
      key: 'prepared' | 'repertoire' | 'animist_prepared' | 'apparition_spontaneous';
      label: string;
      mode: 'Prepared' | 'Spontaneous';
      cantrips: number;
      rank1: number;
      slotsRank1: number;
    }>;
  } | null;
  license: string | null;
  source_book: string | null;
  source_pg: number | null;
  created_at: string;
  updated_at: string;
}

export interface PF2eClassListResponse {
  total: number;
  classes: PF2eClass[];
}

// ==================== Spells (Step 5c) ====================

export interface PF2eSpell {
  id: string;
  name: string;
  localized_name?: string | null;
  localized_description?: string | null;
  localized_tradition?: string | null;
  localized_rarity?: string | null;
  localized_trait?: string | null;
  localized_spell_type?: string | null;
  localized_heighten?: string | null;
  localized_actions?: string | null;
  localized_trigger?: string | null;
  localized_target?: string | null;
  localized_range?: string | null;
  localized_area?: string | null;
  localized_duration?: string | null;
  localized_defense?: string | null;
  localized_heighten_level?: string | null;
  prerequisite?: string | null;
  localized_prerequisite?: string | null;
  requirements?: string | null;
  localized_requirements?: string | null;
  tradition: string | null;
  rarity: string;
  trait: string | null;
  description: string;
  spell_type: string | null;
  rank: string | null;
  heighten: string | null;
  actions: string | null;
  trigger: string | null;
  target: string | null;
  range: string | null;
  area: string | null;
  duration: string | null;
  defense: string | null;
  heighten_level: string | null;
  license: string | null;
  source_book: string | null;
  source_pg: number | null;
  release_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface PF2eSpellListResponse {
  total: number;
  spells: PF2eSpell[];
}

export interface SpellFilterOptions {
  search?: string;
  tradition?: string;
  rarity?: string;
  spell_type?: string;
  rank?: string;
  max_rank?: number;
  locale?: string;
}

// ==================== General Feats (Step 3) ====================

export interface PF2eFeat {
  id: string;
  name: string;
  localized_name?: string | null;
  localized_description?: string | null;
  localized_prerequisite?: string | null;
  pfs: string | null;                    // Pathfinder Society status
  rarity: string;                        // Common, Uncommon, Rare
  trait: string | null;                  // Черты (General, Skill, Class, etc.)
  level: number;                         // Уровень подвига (1-20)
  prerequisite: string | null;           // Требования
  summary: string | null;                // Краткое описание (ancestry_feats)
  description: string | null;            // Описание (feats_class / feats)
  license: string | null;                // Лицензия
  source_book: string | null;            // Источник правил
  source_pg: number | null;              // Страница источника
  raw_rules: Array<Record<string, unknown>> | null;
  slug?: string | null;
  foundry_id?: string | null;
  max_takable: number | null;
  choice_requirements: RuleChoiceRequirement[];
  choice_supported?: boolean;
  choice_unavailable_reason?: string | null;
  created_at: string;
  updated_at: string;
}

export interface PF2eFeatListResponse {
  total: number;
  feats: PF2eFeat[];
}

export interface GrantEntityRef {
  id: string;
  _nameHint?: string | null;
}

export interface RuleSelection {
  grant_rule_id: string;
  selection_schema_version: 1 | 2;
  selected_ref: GrantEntityRef | null;
  choices: Record<string, string | string[]>;
  choice_revisions: Record<string, string>;
}

export interface RuleSourceRef {
  kind:
    | 'heritage'
    | 'versatile_heritage'
    | 'ancestry_feat'
    | 'background'
    | 'feat'
    | 'class_feat'
    | 'class_feature';
  id: string | null;
  key: string;
}

export interface FeatRuleSelector {
  kind: 'feat';
  category: 'general' | 'skill' | 'class' | 'ancestry' | 'archetype';
  max_level: number;
  traits_all: string[];
  class_bound: boolean;
  exclude_current_class?: boolean;
}

export interface RuleChoiceOption {
  value: string;
  label_key: string | null;
  label: string | null;
  available: boolean;
  reason_code: string | null;
}

export interface RuleChoiceRequirement {
  key: string;
  kind: 'skill' | 'literal' | 'entity' | 'free_text';
  required: boolean;
  count: number;
  distinct: boolean;
  revision_hash: string | null;
  source_kind: 'free_text' | 'literal' | 'config' | 'reference' | 'actor_context' | null;
  options: RuleChoiceOption[];
}

export interface CharacterGrantRule {
  id: string;
  schema_version: 1;
  source: RuleSourceRef;
  grant_key: string;
  mode: 'fixed_entity' | 'entity_choice' | 'value_choice' | 'branch_choice';
  target_kind:
    | 'feat'
    | 'class_feat'
    | 'ancestry_feat'
    | 'skill'
    | 'language'
    | 'lore'
    | 'proficiency'
    | 'terrain'
    | 'damage_type'
    | 'natural_attack'
    | 'branch';
  required: boolean;
  count: number;
  application_order: number;
  selector: FeatRuleSelector | null;
  fixed_target_ref: GrantEntityRef | null;
  fixed_choices: Record<string, string | string[]>;
  choice_requirements: RuleChoiceRequirement[];
  choice_effects: Array<{
    kind: 'train_skill' | 'train_lore';
    choice_key: string;
  }>;
  condition: {
    kind: 'background_skill_equals';
    value: string;
  } | null;
  effect: {
    kind: 'grant_feat';
    feat_type: 'general' | 'skill' | 'class' | 'ancestry' | 'archetype';
  } | null;
}

export interface RuleEntityOption {
  ref: GrantEntityRef;
  name: string;
  localized_name: string | null;
  level: number | null;
  traits: string[];
  rarity: string | null;
  prerequisite: string | null;
  localized_prerequisite: string | null;
  description: string | null;
  localized_description: string | null;
  available: boolean;
  availability: 'available' | 'unavailable' | 'deferred';
  reason_codes: string[];
  choice_requirements: RuleChoiceRequirement[];
}

export interface GrantRuleResolution {
  rule: CharacterGrantRule;
  complete: boolean;
  status: 'available' | 'incomplete' | 'invalid' | 'orphaned' | 'unsupported';
  reason_codes: string[];
  options: RuleEntityOption[];
  selected_option: RuleEntityOption | null;
}

export interface CharacterGrantOptionsResponse {
  schema_version: 1;
  rules: GrantRuleResolution[];
}

export interface FeatFilterOptions {
  search?: string;                       // Поиск по названию
  rarity?: string;                       // Фильтр по редкости
  trait?: string;                        // Фильтр по черте
  min_level?: number;                    // Минимальный уровень
  max_level?: number;                    // Максимальный уровень
  ancestry?: string;                     // Контекст происхождения для зависимых вариантов
  ancestry_id?: string;                  // Фильтр по происхождению
  class_name?: string;                   // Фильтр по классу
  locale?: string;                       // Read-only localized_name projection
}

// ==================== Subclass API ====================

export interface PF2eSubclass {
  id: string;
  foundry_id: string;
  name: string;
  localized_name?: string | null;
  localized_description?: string | null;
  localized_tradition?: string | null;
  localized_granted_skills?: string | null;
  class_slug: string;
  subclass_tag: string | null;
  selection_min: number;
  selection_max: number;
  tradition: string | null;
  granted_skills: string | null;
  description: string | null;
  raw_rules: Array<Record<string, unknown>> | Record<string, unknown> | null;
  img: string | null;
  rarity: string;
  license: string | null;
  source_book: string | null;
  source_pg: string | null;
}

export interface PF2eSubclassListResponse {
  total: number;
  subclasses: PF2eSubclass[];
}

// ==================== Deity API ====================

export interface PF2eDeity {
  id: string;
  name: string;
  localized_name?: string | null;
  edict: string | null;
  localized_edict?: string | null;
  anathema: string | null;
  localized_anathema?: string | null;
  pfs: string | null;
  localized_pfs?: string | null;
  domain: string | null;
  localized_domain?: string | null;
  img: string | null;
  rarity: string;
  divine_font: string | null;
  localized_divine_font?: string | null;
  sanctification: string | null;
  localized_sanctification?: string | null;
  ability: string | null;
  localized_ability?: string | null;
  favored_weapon: string | null;
  localized_favored_weapon?: string | null;
  deity_category: string | null;
  localized_deity_category?: string | null;
  pantheon: string | null;
  localized_pantheon?: string | null;
  font: string | null;
  localized_font?: string | null;
  sanctification_modal: string | null;
  sanctification_what: string | null;
  localized_sanctification_what?: string | null;
  skill: string | null;
  localized_skill?: string | null;
  description: string | null;
  localized_description?: string | null;
  source_book: string | null;
  source_pg: string | null;
}

export interface PF2eDeityListResponse {
  total: number;
  deities: PF2eDeity[];
}

// ==================== Language API ====================

export interface PF2eLanguage {
  id: string;
  name: string;
  localized_name?: string | null;
  description: string | null;
  rarity: string;
  source_book: string | null;
  source_pg: string | null;
}

export interface PF2eLanguageListResponse {
  total: number;
  languages: PF2eLanguage[];
}

export interface PF2eCommanderTactic {
  id: string;
  slug: string;
  foundry_id?: string | null;
  name: string;
  localized_name?: string | null;
  summary?: string | null;
  localized_summary?: string | null;
  description?: string | null;
  localized_description?: string | null;
  tier: 'mobility' | 'offensive';
  action_type?: string | null;
  actions?: number | null;
  traits: string[];
  other_tags: string[];
  trigger?: unknown;
  requirements?: unknown;
  frequency?: unknown;
  source_book?: string | null;
  source_pg?: string | null;
  license: 'ORC';
}

export interface PF2eCommanderTacticListResponse {
  total: number;
  folio_size: number;
  prepared_size: number;
  tactics: PF2eCommanderTactic[];
}

// ==================== Source Book API ====================

export interface PF2eSourceBook {
  id: string;
  name: string;
  localized_name?: string | null;
  description: string | null;
  localized_description?: string | null;
  cover_path: string | null;
  release_date: string | null;
  license: string;
  remastered: boolean;
  created_at: string;
  updated_at: string;
}

export interface PF2eSourceBookListResponse {
  total: number;
  source_books: PF2eSourceBook[];
}

export interface WizardBootstrapResponse {
  ancestries: PF2eAncestryListResponse;
  classes: PF2eClassListResponse;
  backgrounds: PF2eBackgroundListResponse;
  versatile_heritages: PF2eVersatileHeritageListResponse;
  general_feats_level1: PF2eFeatListResponse;
  character_grant_rules: CharacterGrantRule[];
}

export const pf2eApi = {
  // Public endpoints (no auth required)
  getAncestries: (opts?: { locale?: string }) => {
    const params = opts ? { params: opts } : {};
    return api.get<PF2eAncestryListResponse>('/pf2e/public/ancestries', params);
  },
  getAncestryById: (id: string, opts?: { locale?: string }) => {
    const params = opts ? { params: opts } : {};
    return api.get<PF2eAncestry>(`/pf2e/public/ancestries/${id}`, params);
  },
  getHeritages: (ancestryId?: string, opts?: { locale?: string }) => {
    const params = { params: { ...(ancestryId ? { ancestry_id: ancestryId } : {}), ...(opts || {}) } };
    return api.get<PF2eHeritageListResponse>('/pf2e/public/ancestry-heritages', params);
  },
  getVersatileHeritages: (opts?: { locale?: string }) => {
    const params = opts ? { params: opts } : {};
    return api.get<PF2eVersatileHeritageListResponse>('/pf2e/public/versatile-heritages', params);
  },
  getAncestryFeats: (opts?: { traits?: string; max_level?: number; locale?: string }) => {
    const params = opts ? { params: opts } : {};
    return api.get<PF2eAncestryFeatListResponse>('/pf2e/public/ancestry-feats', params);
  },
  // General/Skill Feats endpoint (pf2e.feats table)
  getFeats: (filters?: FeatFilterOptions) => {
    const params = filters ? { params: filters } : {};
    return api.get<PF2eFeatListResponse>('/pf2e/public/feats', params);
  },
  // Class Feats endpoint (pf2e.feats_class table)
  getClassFeats: (opts?: { class_name?: string; max_level?: number; search?: string; rarity?: string; locale?: string }) => {
    const params = opts ? { params: opts } : {};
    return api.get<PF2eFeatListResponse>('/pf2e/public/class-feats', params);
  },
  // Backgrounds endpoint (Step 4)
  getBackgrounds: (filters?: { search?: string; rarity?: string; locale?: string }) => {
    const params = filters ? { params: filters } : {};
    return api.get<PF2eBackgroundListResponse>('/pf2e/public/backgrounds', params);
  },
  // Classes endpoint (Step 5)
  getClasses: (filters?: { search?: string; rarity?: string; is_spellcaster?: boolean; locale?: string }) => {
    const params = filters ? { params: filters } : {};
    return api.get<PF2eClassListResponse>('/pf2e/public/classes', params);
  },
  // Spells endpoint (Step 5c)
  getSpells: (filters?: SpellFilterOptions) => {
    const params = filters ? { params: filters } : {};
    return api.get<PF2eSpellListResponse>('/pf2e/public/spells', params);
  },
  getVisions: (opts?: { locale?: string }) => {
    const params = opts ? { params: opts } : {};
    return api.get<PF2eVisionListResponse>('/pf2e/public/visions', params);
  },
  // Subclasses endpoint (Step 5e)
  getSubclasses: (opts?: { class_slug?: string; subclass_tag?: string; search?: string; level?: number; locale?: string }) => {
    const params = opts ? { params: opts } : {};
    return api.get<PF2eSubclassListResponse>('/pf2e/public/subclasses', params);
  },
  // Deities endpoint (Step 5d)
  getDeities: (opts?: { search?: string; rarity?: string; locale?: string }) => {
    const params = opts ? { params: opts } : {};
    return api.get<PF2eDeityListResponse>('/pf2e/public/deities', params);
  },
  // Languages endpoint (Step 7 / GAP-06)
  getLanguages: (opts?: { search?: string; locale?: string }) => {
    const params = opts ? { params: opts } : {};
    return api.get<PF2eLanguageListResponse>('/pf2e/public/languages', params);
  },
  getCommanderTactics: (opts?: { locale?: string }) => {
    const params = opts ? { params: opts } : {};
    return api.get<PF2eCommanderTacticListResponse>('/pf2e/public/commander/tactics', params);
  },
  getSourceBooks: (opts?: { locale?: string }) => {
    const params = opts ? { params: opts } : {};
    return api.get<PF2eSourceBookListResponse>('/pf2e/public/source-books', params);
  },
  getConditions: (opts?: { locale?: string }) => {
    const params = opts ? { params: opts } : {};
    return api.get<PF2eConditionListResponse>('/pf2e/public/conditions', params);
  },
  getTraitsBatch: (traits: string[], locale?: string) => (
    api.post<PF2eTraitBatchResponse>('/pf2e/public/traits/batch', { traits, locale })
  ),
  resolveOrcLabels: (targets: string[], locale?: string) => {
    const params = { targets: targets.join(','), ...(locale ? { locale } : {}) };
    return api.get<{ labels: Record<string, string> }>('/pf2e/public/orc-labels', { params });
  },
  getKnowledgeEntry: (targetType: string, slug: string, locale?: string) => {
    const params = locale ? { locale } : {};
    return api.get<PF2eKnowledgeArticleResponse>(
      `/pf2e/public/knowledge/entries/${encodeURIComponent(targetType)}/${encodeURIComponent(slug)}`,
      { params }
    );
  },
  getOrcTarget: (targetType: string, slug: string, locale?: string) => {
    const params = locale ? { locale } : {};
    return api.get<PF2eKnowledgeArticleResponse>(
      `/pf2e/public/orc-target/${encodeURIComponent(targetType)}/${encodeURIComponent(slug)}`,
      { params }
    );
  },
  getKnowledgeEntriesBatch: (targets: string[], locale?: string) => {
    return api.post<PF2eKnowledgeBatchResponse>('/pf2e/public/knowledge/entries/batch', { targets, locale });
  },
  searchKnowledge: (opts?: { q?: string; locale?: string; type?: string; category?: string; limit?: number; offset?: number }) => {
    const params = opts || {};
    return api.get<PF2eKnowledgeSearchResponse>('/pf2e/public/knowledge/search', { params });
  },
  getKnowledgeCategories: () => {
    return api.get<PF2eKnowledgeCategoriesResponse>('/pf2e/public/knowledge/categories');
  },
  getVocabulary: (locale?: string) => {
    const params = locale ? { locale } : {};
    return api.get<{ locale: string; vocabulary: Record<string, any> }>('/pf2e/public/vocabulary', { params });
  },
  getWizardBootstrap: (opts?: { locale?: string }) => {
    const params = opts ? { params: opts } : {};
    return api.get<WizardBootstrapResponse>('/pf2e/public/wizard-bootstrap', params);
  },
  getCharacterGrantOptions: (
    body: {
      ancestry_id?: string | null;
      heritage_id?: string | null;
      ancestry_feat_id?: string | null;
      background_id?: string | null;
      deity_id?: string | null;
      background_skill_choice?: string | null;
      class_name?: string;
      selections?: RuleSelection[];
    },
    opts?: { locale?: string },
  ) => {
    const params = opts?.locale ? { locale: opts.locale } : {};
    return api.post<CharacterGrantOptionsResponse>(
      '/pf2e/public/character-grants/options',
      body,
      { params },
    );
  },
};

export type PF2eKnowledgeStatus =
  | 'ok'
  | 'missing'
  | 'blocked_non_orc'
  | 'blocked_license_unknown'
  | 'ambiguous'
  | 'deferred_journal'
  | 'synthetic_token';

export interface PF2eKnowledgeTarget {
  type: string;
  slug: string;
  key: string;
  requested_type?: string | null;
  requested_slug?: string | null;
}

export interface PF2eKnowledgeSource {
  title?: string | null;
  page?: string | null;
  license?: string | null;
}

export interface PF2eKnowledgeArticleResponse {
  target: PF2eKnowledgeTarget;
  status: PF2eKnowledgeStatus;
  title?: string | null;
  localized_title?: string | null;
  localized: boolean;
  summary?: string | null;
  body_html?: string | null;
  body_rich_text?: string | null;
  category?: string | null;
  rank?: string | null;
  traits: string[];
  source?: PF2eKnowledgeSource | null;
  links: Array<{ type: string; slug: string; label?: string | null; status: string }>;
  metadata: Record<string, unknown>;
  alias?: { kind: string; value?: unknown } | null;
  message_key?: string | null;
  debug?: Record<string, unknown> | null;
}

export interface PF2eKnowledgeBatchResponse {
  entries: Record<string, PF2eKnowledgeArticleResponse>;
}

export interface PF2eKnowledgeSearchItem {
  target: PF2eKnowledgeTarget;
  title: string;
  localized_title?: string | null;
  localized: boolean;
  summary?: string | null;
  category?: string | null;
  rank?: string | null;
  source?: PF2eKnowledgeSource | null;
  match: { fields: string[]; score: number };
}

export interface PF2eKnowledgeSearchResponse {
  total: number;
  items: PF2eKnowledgeSearchItem[];
}

export interface PF2eKnowledgeCategoriesResponse {
  categories: Array<{ key: string; label: string; count: number }>;
}

export interface PF2eCondition {
  id: string;
  slug: string;
  name: string;
  localized_name?: string | null;
  description: string;
  localized_description?: string | null;
  is_valued: boolean;
  default_value: number;
  source_book: string;
  license: string;
  remastered: boolean;
  created_at: string;
  updated_at: string;
}

export interface PF2eConditionListResponse {
  total: number;
  conditions: PF2eCondition[];
}

export interface PF2eTrait {
  id: string;
  slug: string;
  name: string;
  localized_name: string | null;
  description: string;
  localized_description: string | null;
  source_book: string;
  source_pg: string | null;
  license: 'ORC';
  aon_url: string;
}

export interface PF2eTraitBatchResponse {
  entries: Record<string, PF2eTrait | null>;
}

// ==================== Vision API ====================

export interface PF2eVision {
  id: string;
  priority: number;
  name: string;
  localized_name?: string | null;
  localized_description?: string | null;
  description: string;
  license: string | null;
  source_book: string | null;
  source_pg: number | null;
  created_at: string;
  updated_at: string;
}

export interface PF2eVisionListResponse {
  total: number;
  visions: PF2eVision[];
}

// ==================== Creature API (Encounter Builder) ====================

export interface CreatureListItem {
  id: string;
  name: string;
  localized_name?: string | null;
  creature_family: string | null;
  localized_creature_family?: string | null;
  rarity: string;
  localized_rarity?: string | null;
  size: string;
  localized_size?: string | null;
  trait: string | null;
  level: number;
  hp: number;
  ac: number;
  has_statblock: boolean;
  statblock_completeness: 'complete' | 'partial' | null;
  statblock_variant_count: number;
}

export interface CreatureStatblockVariantSummary {
  key: string;
  label: string;
  foundry_slug: string | null;
  completeness: 'complete' | 'partial';
}

export interface Creature extends CreatureListItem {
  fortitude: string;
  reflex: string;
  will: string;
  perception: number;
  sense: string | null;
  localized_sense?: string | null;
  speed: string | null;
  localized_speed?: string | null;
  strength: number;
  dexterity: number;
  constitution: number;
  intelligence: number;
  wisdom: number;
  charisma: number;
  creature_ability: string | null;
  localized_creature_ability?: string | null;
  license: string | null;
  source_book: string | null;
  source_pg: string | null;
  created_at: string;
  updated_at: string;
  recall_knowledge: CreatureStatblockRecallKnowledge;
  statblock: CreatureStatblock | null;
  statblock_variants: CreatureStatblockVariantSummary[];
}

export type CreatureStatblock = CreatureStatblockResponse;

export interface CreatureListResponse {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
  creatures: CreatureListItem[];
}

export interface CreatureFilterOptions {
  search?: string;
  creature_family?: string;
  trait?: string;
  source_book?: string;
  size?: string;
  rarity?: string;
  min_level?: number;
  max_level?: number;
  party_level?: number;
  locale?: string;
  limit?: number;
  offset?: number;
  sort_by?: 'name' | 'level' | 'rarity' | 'ac' | 'hp' | 'size' | 'creature_family' | 'party_level_distance';
  sort_direction?: 'asc' | 'desc';
}

export const creatureApi = {
  getCreatures: (filters?: CreatureFilterOptions, signal?: AbortSignal) => (
    api.get<CreatureListResponse>('/pf2e/public/creatures', {
      params: filters,
      signal,
    })
  ),
  getCreatureById: (
    id: string,
    locale?: string,
    statblockVariant?: string,
    signal?: AbortSignal,
  ) => api.get<Creature>(
    `/pf2e/public/creatures/${id}`,
    {
      params: {
        ...(locale ? { locale } : {}),
        ...(statblockVariant ? { statblock_variant: statblockVariant } : {}),
      },
      signal,
    },
  ),
};

// ==================== Equipment API (Treasure Planner) ====================

export interface Equipment {
  id: string;
  name: string;
  localized_name: string | null;
  pfs: string | null;
  rarity: string;
  localized_rarity: string | null;
  trait: string | null;
  localized_trait: string | null;
  item_category: string;
  item_subcategory: string | null;
  level: number;
  price: string | null;
  price_cp: number | null;
  bulk: string | null;
  usage: string | null;
  spoilers: string | null;
  license: string | null;
  source_book: string | null;
  source_book_id: string | null;
  source_pg: string | null;
  slug: string | null;
  foundry_id: string | null;
  description: string | null;
  localized_description: string | null;
  prerequisite: string | null;
  localized_prerequisite: string | null;
  requirements: string | null;
  localized_requirements: string | null;
  foundry_type: string | null;
  weapon_category: string | null;
  weapon_group: string | null;
  damage_dice: number | null;
  damage_die: string | null;
  damage_type: string | null;
  range_ft: number | null;
  reload_value: string | null;
  ammo_type: string | null;
  armor_category: string | null;
  armor_group: string | null;
  ac_bonus: number | null;
  dex_cap: number | null;
  check_penalty: number | null;
  speed_penalty: number | null;
  strength_threshold: number | null;
  shield_ac_bonus: number | null;
  hardness: number | null;
  shield_hp_max: number | null;
  broken_threshold: number | null;
  created_at: string;
  updated_at: string;
}

export interface EquipmentListResponse {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
  equipment: Equipment[];
}

export type EquipmentDetailResponse = Equipment;

export type EquipmentSortColumn =
  | 'name'
  | 'level'
  | 'rarity'
  | 'price'
  | 'item_category'
  | 'item_subcategory'
  | 'source_book';

export interface EquipmentFilterOptions {
  search?: string;
  locale?: string;
  item_category?: string;
  item_subcategory?: string;
  trait?: string;
  source_book?: string;
  rarity?: string;
  min_level?: number;
  max_level?: number;
  foundry_type?: string;
  weapon_category?: string;
  is_melee?: boolean;
  is_ranged?: boolean;
  armor_category?: string;
  limit?: number;
  offset?: number;
  sort_by?: EquipmentSortColumn;
  sort_direction?: 'asc' | 'desc';
}

export const equipmentApi = {
  getEquipment: (filters?: EquipmentFilterOptions, signal?: AbortSignal) => api.get<EquipmentListResponse>(
    '/pf2e/public/equipment',
    { params: filters, signal },
  ),
  getEquipmentById: (id: string, locale?: string, signal?: AbortSignal) => api.get<EquipmentDetailResponse>(
    `/pf2e/public/equipment/${id}`,
    { params: locale ? { locale } : {}, signal },
  ),
};

// ==================== Level-Up Planner API ====================

export type SlotType =
  | 'class_feat' | 'skill_feat' | 'general_feat' | 'ancestry_feat' | 'archetype_feat'
  | 'skill_increase' | 'ability_boost' | 'spell_known' | 'focus_spell' | 'language'
  | 'spell_book_addition';

export type PlanStatus = 'draft' | 'committed' | 'stale';
export type IntegrityStatus = 'ok' | 'stale' | 'incompatible';

export interface SlotDefinition {
  level: number;
  slot_type: SlotType;
  slot_index: number;
  source: string;
  class_name: string | null;
  filter_tags: string[];
}

export interface SkillIncreaseOption {
  name: string;
  slug: string;
  current_rank: 'Untrained' | 'Trained' | 'Expert' | 'Master';
  next_rank: 'Trained' | 'Expert' | 'Master' | 'Legendary';
}

export interface LevelPlanEventCreate {
  level: number;
  slot_type: SlotType;
  slot_index: number;
  chosen_item_type?: string | null;
  chosen_item_id?: string | null;
  chosen_item_slug?: string | null;
  chosen_item_name?: string | null;
  extra_data?: Record<string, unknown> | null;
  rule_condition_confirmations?: RuleConditionConfirmationInput[];
  access_acknowledgements?: AccessAcknowledgementInput[];
}

export interface LevelPlanConditionOption extends RuleEntityRef {
  level: number;
  slot_type: Extract<
    SlotType,
    | 'class_feat'
    | 'skill_feat'
    | 'general_feat'
    | 'ancestry_feat'
    | 'archetype_feat'
    | 'spell_known'
    | 'focus_spell'
    | 'spell_book_addition'
  >;
  slot_index: number;
}

export interface LevelPlanEventRead extends LevelPlanEventCreate {
  id: number;
  plan_id: number;
  localized_chosen_item_name?: string | null;
  confirmed_prerequisites: RuleConditionConfirmationInput[];
  confirmed_access: AccessAcknowledgementInput[];
  created_at: string;
}

export interface VariantRules {
  free_archetype: boolean;
  ancestral_paragon: boolean;
  gradual_boosts: boolean;
}

export interface LevelPlanRead {
  id: number;
  character_id: string;
  target_level: number;
  status: PlanStatus;
  mode: string;
  variant_rules: VariantRules;
  integrity_status: IntegrityStatus;
  baseline_hash: string | null;
  events: LevelPlanEventRead[];
  validation_issues: ValidationIssue[];
  created_at: string;
  updated_at: string;
}

export interface ValidationIssue {
  level: number;
  slot_type: string;
  slot_index: number;
  issue: string;
  severity: 'error' | 'warning';
}

export interface ValidationResult {
  valid: boolean;
  issues: ValidationIssue[];
}

export const levelPlanApi = {
  /** Returns the single living plan (auto-created server-side on first access). */
  get: (characterId: string, locale?: string): Promise<LevelPlanRead> =>
    api.get<LevelPlanRead>(`/characters/${characterId}/level-plan`, { params: { locale } })
      .then(r => r.data),

  /** Update variant rules on the living plan. */
  setVariantRules: (characterId: string, variantRules: Partial<VariantRules>): Promise<LevelPlanRead> =>
    api.patch<LevelPlanRead>(`/characters/${characterId}/level-plan/mode`, {
      mode: 'sequential', variant_rules: variantRules,
    }).then(r => r.data),

  upsertEvent: (characterId: string, event: LevelPlanEventCreate, locale?: string): Promise<LevelPlanEventRead> =>
    api.patch<LevelPlanEventRead>(`/characters/${characterId}/level-plan/events`, event, { params: { locale } })
      .then(r => r.data),

  assessConditions: (
    characterId: string,
    options: LevelPlanConditionOption[],
    locale?: string,
  ): Promise<RuleConditionAssessmentResponse> => api.post<RuleConditionAssessmentResponse>(
    `/characters/${characterId}/level-plan/condition-assessments`,
    { options, locale },
  ).then(r => r.data),

  deleteEvent: (characterId: string, eventId: number): Promise<void> =>
    api.delete(`/characters/${characterId}/level-plan/events/${eventId}`)
      .then(() => undefined),

  getSlots: (characterId: string, targetLevel: number): Promise<SlotDefinition[]> =>
    api.get<SlotDefinition[]>(`/characters/${characterId}/level-plan/slots`, {
      params: { target_level: targetLevel },
    }).then(r => r.data),

  getSkillIncreaseOptions: (
    characterId: string,
    level: number,
    slotIndex: number,
  ): Promise<SkillIncreaseOption[]> =>
    api.get<SkillIncreaseOption[]>(
      `/characters/${characterId}/level-plan/skill-increase-options`,
      { params: { level, slot_index: slotIndex } },
    ).then(r => r.data),

  /** Per-level grant-summary slot-type tags for the level-rail chips. */
  getGrants: (characterId: string): Promise<Record<string, string[]>> =>
    api.get<{ grants: Record<string, string[]> }>(`/characters/${characterId}/level-plan/grants`)
      .then(r => r.data.grants),

  /** Advance the character one level (current+1); requires that level's slots filled. */
  levelUp: (characterId: string): Promise<Character> =>
    api.post<Character>(`/characters/${characterId}/level-plan/level-up`)
      .then(r => r.data),

  previewCompanions: (characterId: string): Promise<{ slots: CompanionSlot[] }> =>
    api.post<{ slots: CompanionSlot[] }>(`/characters/${characterId}/level-plan/companion-preview`).then(r => r.data),

  levelUpWithCompanions: (characterId: string, bindings: CompanionBindingRequest[]): Promise<Character> =>
    api.post<Character>(`/characters/${characterId}/level-plan/level-up-with-companions`, { bindings }).then(r => r.data),

  /** Roll the character back to level 1 and clear all level 2+ choices. */
  reset: (characterId: string): Promise<Character> =>
    api.post<Character>(`/characters/${characterId}/level-plan/reset`)
      .then(r => r.data),
};

// ==================== Localization API ====================

export interface LocalizationKey {
  id: string;
  namespace: string;
  key: string;
  english_value: string;
  source_file: string;
  source_hash: string;
  source_component: string | null;
  source_kind: string;
  source_ref: string | null;
  revision: number;
  created_at: string;
  updated_at: string;
  existing_translation: string | null;
  is_confirmed: boolean;
  is_pending: boolean;
  status: 'missing' | 'machine' | 'official' | 'confirmed' | 'pending' | 'stale';
  translation_revision: number;
  translation_review_status: 'missing' | 'approved' | 'revoked' | 'stale';
}

export interface LocalizationKeyListResponse {
  total: number;
  keys: LocalizationKey[];
  page: number;
  page_size: number;
  total_pages: number;
  source_files: string[];
}

export interface PF2eTranslationEntry {
  id: string;
  locale: string;
  entity_table: string;
  entity_id: string;
  entity_name: string | null;
  field_name: string;
  source_text: string | null;
  source_text_hash: string;
  translated_text: string;
  status: 'missing' | 'machine' | 'official' | 'confirmed';
  source_kind: string | null;
  source_ref: string | null;
  orc_targets: Array<Record<string, string>>;
  review_status: 'needs_review' | 'approved' | 'stale' | 'rejected' | 'blocked';
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  last_review_run_id: string | null;
  risk_bucket: string;
  is_stale: boolean;
  validation_warnings: string[];
  created_at: string;
  updated_at: string;
}

export interface PF2eTranslationEntryListResponse {
  total: number;
  entries: PF2eTranslationEntry[];
  page: number;
  page_size: number;
  total_pages: number;
  entity_tables: string[];
  field_names: string[];
}

export interface PF2eMemoryCandidate {
  entry_kind: 'translation' | 'vocabulary';
  id: string;
  locale: string;
  status: 'missing' | 'machine' | 'official' | 'confirmed';
  review_status: 'needs_review' | 'approved' | 'stale' | 'rejected' | 'blocked';
  source_text: string | null;
  translated_text: string;
  source_kind: string | null;
  source_ref: string | null;
  entity_table?: string | null;
  entity_id?: string | null;
  entity_name?: string | null;
  field_name?: string | null;
  scope?: string | null;
  key?: string | null;
  updated_at: string;
}

export interface PF2eMemoryTerm {
  id: string;
  scope: string;
  term_key: string;
  source_text: string;
  approved_text: string;
  status: 'locked' | 'preferred' | 'deprecated';
  source_kind: string | null;
  source_ref: string | null;
}

export interface PF2eMemoryReport {
  entry_kind: 'translation' | 'vocabulary';
  id: string;
  locale: string;
  source_text: string | null;
  source_text_hash: string;
  exact_matches: PF2eMemoryCandidate[];
  same_entity_matches: PF2eMemoryCandidate[];
  terms: PF2eMemoryTerm[];
}

export interface PF2eSourceCandidate {
  source: string;
  source_type: string;
  trust_level: string;
  lang: string;
  key: string;
  english: string;
  target: string;
  text: string;
  path: string;
  status: string;
  review_status: string;
  provenance: string;
  rank: number;
}

export interface PF2eSourceContext {
  entry_kind: 'translation';
  id: string;
  locale: string;
  query: string;
  available: boolean;
  reason: string | null;
  index_path: string;
  index_updated_at: string | null;
  candidates: PF2eSourceCandidate[];
}

export interface PF2eSourceFinderMatch {
  lang: string;
  path: string;
  line_number: number;
  matched_text: string;
  snippet: string;
  source_ref_match: boolean;
}

export interface PF2eSourceFinder {
  entry_kind: 'translation';
  id: string;
  locale: string;
  query: string;
  available: boolean;
  reason: string | null;
  root_path: string;
  matches: PF2eSourceFinderMatch[];
}

export interface PF2eVocabularyEntry {
  id: string;
  scope: string;
  key: string;
  locale: string;
  source_text: string;
  source_text_hash: string;
  translated_text: string;
  status: 'missing' | 'machine' | 'official' | 'confirmed';
  source_kind: string | null;
  source_ref: string | null;
  review_status: 'needs_review' | 'approved' | 'stale' | 'rejected' | 'blocked';
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  last_review_run_id: string | null;
  risk_bucket: string;
  is_stale: boolean;
  created_at: string;
  updated_at: string;
}

export interface PF2eVocabularyEntryListResponse {
  total: number;
  entries: PF2eVocabularyEntry[];
  page: number;
  page_size: number;
  total_pages: number;
  scopes: string[];
}

export interface PF2eTranslationTerm {
  id: string;
  locale: string;
  scope: string;
  term_key: string;
  source_text: string;
  approved_text: string;
  status: 'locked' | 'preferred' | 'deprecated';
  source_kind: string | null;
  source_ref: string | null;
  reviewer_id: string | null;
  reviewed_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface PF2eTranslationTermListResponse {
  total: number;
  terms: PF2eTranslationTerm[];
  page: number;
  page_size: number;
  total_pages: number;
  scopes: string[];
}

export interface PF2eTranslationTermUpsertRequest {
  locale?: string;
  scope?: string;
  term_key: string;
  source_text: string;
  approved_text: string;
  status?: 'locked' | 'preferred' | 'deprecated';
  source_kind?: string;
  source_ref?: string;
  notes?: string;
}

export interface PF2eTranslationVersion {
  id: string;
  translation_entry_id: string | null;
  vocabulary_entry_id: string | null;
  locale: string;
  action: string;
  before_translated_text: string | null;
  after_translated_text: string | null;
  before_status: string | null;
  after_status: string | null;
  before_review_status: string | null;
  after_review_status: string | null;
  before_source_text_hash: string | null;
  after_source_text_hash: string | null;
  before_source_kind: string | null;
  after_source_kind: string | null;
  before_source_ref: string | null;
  after_source_ref: string | null;
  before_orc_targets: Array<Record<string, string>> | null;
  after_orc_targets: Array<Record<string, string>> | null;
  actor_user_id: string | null;
  actor_kind: string;
  actor_ref: string | null;
  source_kind: string | null;
  source_ref: string | null;
  run_id: string | null;
  reason: string | null;
  created_at: string;
}

export interface PF2eReviewActionRequest {
  action: 'save_draft' | 'approve' | 'reject' | 'mark_stale' | 'revalidate' | 'edit_and_approve' | 'block';
  translated_text?: string;
  status?: 'missing' | 'machine' | 'official' | 'confirmed';
  source_kind?: string;
  source_ref?: string;
  review_note?: string;
  run_id?: string;
  expected_updated_at: string;
}

export interface RuntimeReleaseLocalePreview {
  status: string;
  content_sha256: string;
  current_content_sha256: string | null;
  raw_bytes: number;
  raw_bytes_delta: number;
  encoded_bytes: number;
  required_keys: number;
  required_missing: number;
  optional_missing: number;
  coverage_percent: number;
  segments: Record<string, Record<string, string | number | boolean>>;
  warnings: Array<Record<string, unknown>>;
  changes: {
    added: number;
    modified: number;
    removed: number;
    sample_keys: string[];
  };
}

export interface RuntimeReleasePreview {
  complete: boolean;
  publication_run_id: string;
  candidate_release_id: string;
  active_release_id: string | null;
  previous_release_id: string | null;
  source_digest: string;
  current_source_digest: string | null;
  source_changed: boolean;
  warning_count: number;
  locales: Record<string, RuntimeReleaseLocalePreview>;
}

export interface RuntimeLocaleManifest {
  version: string;
  generated_at: string;
  mode: 'release';
  release_id: string | null;
  previous_release_id: string | null;
  locales: Record<string, Record<string, unknown>>;
}

export interface RuntimeReleaseEvent {
  id: string;
  action: 'publish' | 'rollback';
  run_id: string;
  actor_user_id: string | null;
  actor_kind: 'user' | 'cli' | 'system';
  actor_ref: string;
  from_release_id: string | null;
  to_release_id: string;
  created_at: string;
}

export interface RuntimeReleaseHealth {
  status: 'ready' | 'degraded' | 'blocked';
  mode: 'release';
  active_release_id: string | null;
  previous_release_id: string | null;
  candidate_release_id: string | null;
  candidate_complete: boolean;
  candidate_source_changed: boolean;
  last_publication_at: string | null;
  last_publication_action: 'publish' | 'rollback' | null;
  locales: Record<string, {
    active_status: string;
    candidate_status: string;
    coverage_percent: number | null;
    required_missing: number | null;
    optional_missing: number | null;
  }>;
  metrics: {
    switch_attempts?: number;
    switch_failures?: number;
    switch_failure_rate?: number;
    counts?: Record<string, number>;
    alerts?: Array<{ code: string; last_triggered_at: string }>;
  };
  generated_at: string;
}

export interface PF2eReviewQueueItem {
  entry_kind: 'translation' | 'vocabulary';
  id: string;
  locale: string;
  status: 'missing' | 'machine' | 'official' | 'confirmed';
  review_status: 'needs_review' | 'approved' | 'stale' | 'rejected' | 'blocked';
  risk_bucket: string;
  source_text: string | null;
  translated_text: string;
  source_kind: string | null;
  source_ref: string | null;
  is_stale: boolean;
  validation_warnings: string[];
  entity_table?: string | null;
  entity_id?: string | null;
  entity_name?: string | null;
  field_name?: string | null;
  scope?: string | null;
  key?: string | null;
  updated_at: string;
}

export interface PF2eReviewQueueResponse {
  total: number;
  entries: PF2eReviewQueueItem[];
  page: number;
  page_size: number;
  total_pages: number;
}

export interface PF2eReviewQueueExportRequest {
  locale?: string;
  entry_kind?: 'translation' | 'vocabulary';
  status?: 'missing' | 'machine' | 'official' | 'confirmed';
  review_status?: 'needs_review' | 'approved' | 'stale' | 'rejected' | 'blocked';
  risk_bucket?: string;
  entity_table?: string;
  field_name?: string;
  scope?: string;
  search?: string;
  limit?: number;
  run_id?: string;
}

export interface PF2eReviewQueueExportResponse {
  run_id: string;
  exported: number;
  jsonl: string;
}

export interface PF2eReviewQueueImportRequest {
  jsonl: string;
  apply?: boolean;
  allow_stale?: boolean;
  run_id?: string;
}

export interface PF2eReviewQueueImportResponse {
  run_id: string;
  mode: 'dry_run' | 'apply';
  counts: Record<string, number>;
}

export interface LocalizationProposal {
  id: string;
  key_id: string;
  namespace: string;
  key: string;
  english_value: string;
  language: string;
  translated_value: string;
  proposed_by: string | null;
  proposer_email: string | null;
  comment: string | null;
  base_revision: number;
  source_text_hash: string;
  proposed_at: string;
}

export interface GroupedProposalItem {
  proposal_id: string;
  translated_value: string;
  proposed_by: string | null;
  proposer_email: string | null;
  comment: string | null;
  base_revision: number;
  source_text_hash: string;
  proposed_at: string;
}

export interface UiLocalizationTranslationVersion {
  id: string;
  key_id: string;
  language: string;
  action: string;
  before_translated_value: string | null;
  after_translated_value: string | null;
  before_status: string | null;
  after_status: string | null;
  before_review_status: string | null;
  after_review_status: string | null;
  before_source_text_hash: string | null;
  after_source_text_hash: string | null;
  before_source_kind: string | null;
  after_source_kind: string | null;
  before_source_ref: string | null;
  after_source_ref: string | null;
  before_revision: number;
  after_revision: number;
  actor_user_id: string | null;
  actor_kind: string;
  actor_ref: string | null;
  run_id: string | null;
  reason: string | null;
  created_at: string;
}

export interface GroupedProposalsResponse {
  key_id: string;
  namespace: string;
  key: string;
  english_value: string;
  language: string;
  proposals: GroupedProposalItem[];
  total_proposals: number;
  is_update: boolean;
  confirmed_value: string | null;
}

export interface LanguageStats {
  language: string;
  language_name: string;
  flag: string;
  total_keys: number;
  translated_keys: number;
  confirmed_keys: number;
  pending_keys: number;
  percentage: number;
  ui_keys: number;
  ui_translated: number;
  ui_confirmed: number;
  pf2e_keys: number;
  pf2e_translated: number;
  pf2e_confirmed: number;
  pf2e_needs_review: number;
  pf2e_stale: number;
  pf2e_blocked: number;
  pf2e_rich_text_backlog: number;
  vocabulary_keys: number;
  vocabulary_translated: number;
  vocabulary_confirmed: number;
  vocabulary_needs_review: number;
}

export interface TopLocalizator {
  user_id: string;
  email: string;
  proposals_count: number;
  approved_count: number;
}

export interface LocalizationSourceSnapshotStatus {
  available: boolean;
  status: 'current' | 'stale' | 'missing' | 'error' | string;
  path: string | null;
  updated_at: string | null;
  age_hours: number | null;
  stale_after_hours: number;
  reason: string | null;
}

export interface LocalizationFreshnessStatus extends LocalizationSourceSnapshotStatus {
  document_count: number;
  languages: Record<string, number>;
  source_types: Record<string, number>;
  snapshots: Record<string, LocalizationSourceSnapshotStatus>;
}

export interface LocalizationStatsResponse {
  total_keys: number;
  total_proposals: number;
  languages: LanguageStats[];
  active_localizators: number;
  top_localizators: TopLocalizator[];
  rag_freshness: LocalizationFreshnessStatus;
  source_freshness: LocalizationFreshnessStatus;
}

export const localizationApi = {
  // Keys
  getKeys: (params?: { page?: number; page_size?: number; namespace?: string; language?: string; search?: string; status?: string; source_file?: string }) =>
    api.get<LocalizationKeyListResponse>('/localization/keys', { params }),

  registerKey: (data: {
    namespace?: 'ui';
    key: string;
    english_value: string;
    source_file: string;
    source_component?: string;
    source_kind?: string;
    source_ref?: string;
  }) => api.post<LocalizationKey>('/localization/keys/register', data),

  getPf2eEntries: (params?: { page?: number; page_size?: number; locale?: string; entity_table?: string; field_name?: string; search?: string; status?: string; review_status?: string; risk_bucket?: string; has_orc?: boolean; has_warnings?: boolean }) =>
    api.get<PF2eTranslationEntryListResponse>('/localization/pf2e/entries', { params }),

  reviewPf2eEntry: (entryId: string, data: PF2eReviewActionRequest) =>
    api.post<PF2eTranslationEntry>(`/localization/pf2e/entries/${entryId}/review`, data),

  getPf2eEntryHistory: (entryId: string) =>
    api.get<PF2eTranslationVersion[]>(`/localization/pf2e/entries/${entryId}/history`),

  getPf2eEntryMemoryReport: (entryId: string) =>
    api.get<PF2eMemoryReport>(`/localization/pf2e/entries/${entryId}/memory-report`),

  getPf2eEntrySourceContext: (entryId: string, params?: { limit?: number }) =>
    api.get<PF2eSourceContext>(`/localization/pf2e/entries/${entryId}/source-context`, { params }),

  getPf2eEntrySourceFinder: (entryId: string, params?: { limit?: number }) =>
    api.get<PF2eSourceFinder>(`/localization/pf2e/entries/${entryId}/source-finder`, { params }),

  getPf2eVocabularyEntries: (params?: { page?: number; page_size?: number; locale?: string; scope?: string; search?: string; status?: string; review_status?: string }) =>
    api.get<PF2eVocabularyEntryListResponse>('/localization/pf2e/vocabulary', { params }),

  reviewPf2eVocabularyEntry: (entryId: string, data: PF2eReviewActionRequest) =>
    api.post<PF2eVocabularyEntry>(`/localization/pf2e/vocabulary/${entryId}/review`, data),

  getPf2eVocabularyHistory: (entryId: string) =>
    api.get<PF2eTranslationVersion[]>(`/localization/pf2e/vocabulary/${entryId}/history`),

  getPf2eVocabularyMemoryReport: (entryId: string) =>
    api.get<PF2eMemoryReport>(`/localization/pf2e/vocabulary/${entryId}/memory-report`),

  getPf2eReviewQueue: (params?: { page?: number; page_size?: number; locale?: string; entry_kind?: string; search?: string; status?: string; review_status?: string; risk_bucket?: string }) =>
    api.get<PF2eReviewQueueResponse>('/localization/pf2e/review-queue', { params }),

  exportPf2eReviewQueue: (data: PF2eReviewQueueExportRequest) =>
    api.post<PF2eReviewQueueExportResponse>('/localization/pf2e/review-queue/export', data),

  importPf2eReviewQueue: (data: PF2eReviewQueueImportRequest) =>
    api.post<PF2eReviewQueueImportResponse>('/localization/pf2e/review-queue/import', data),

  getPf2eTerms: (params?: { page?: number; page_size?: number; locale?: string; scope?: string; status?: string; search?: string }) =>
    api.get<PF2eTranslationTermListResponse>('/localization/pf2e/terms', { params }),

  upsertPf2eTerm: (data: PF2eTranslationTermUpsertRequest) =>
    api.post<PF2eTranslationTerm>('/localization/pf2e/terms', data),

  // Proposals
  propose: (data: { key_id: string; language: string; translated_value: string; comment?: string }) =>
    api.post<LocalizationProposal>('/localization/propose', data),

  getProposals: (language?: string) => {
    const params = language ? { params: { language } } : {};
    return api.get<LocalizationProposal[]>('/localization/proposals', params);
  },

  // Confirmation
  getGroupedProposals: (language?: string) => {
    const params = language ? { params: { language } } : {};
    return api.get<GroupedProposalsResponse[]>('/localization/proposals/grouped', params);
  },

  approveProposal: (proposal_id: string) =>
    api.post('/localization/approve', { proposal_id }),

  rejectProposal: (proposal_id: string) =>
    api.post('/localization/reject', { proposal_id }),

  revokeTranslation: (key_id: string, language: string, expected_revision: number, reason?: string) =>
    api.post<{
      success: boolean;
      translation: {
        revision: number;
        review_status: 'approved' | 'revoked' | 'stale';
      };
    }>('/localization/revoke', { key_id, language, expected_revision, reason }),

  getUiTranslationHistory: (keyId: string, language: string) =>
    api.get<UiLocalizationTranslationVersion[]>(
      `/localization/ui/translations/${keyId}/${language}/history`,
    ),

  rollbackUiTranslation: (
    keyId: string,
    language: string,
    data: { version_id: string; expected_revision: number; reason?: string },
  ) => api.post(
    `/localization/ui/translations/${keyId}/${language}/rollback`,
    data,
  ),

  // Statistics
  getStats: () => api.get<LocalizationStatsResponse>('/localization/stats'),

  previewRuntimeRelease: (publication_run_id?: string) =>
    api.post<RuntimeReleasePreview>('/localization/runtime/releases/preview', {
      publication_run_id,
    }),

  publishRuntimeRelease: (data: { publication_run_id: string; expected_release_id: string }) =>
    api.post<RuntimeLocaleManifest>('/localization/runtime/releases/publish', data),

  rollbackRuntimeRelease: (data: {
    run_id: string;
    expected_active_release_id: string;
    expected_previous_release_id: string;
  }) => api.post<RuntimeLocaleManifest>('/localization/runtime/releases/rollback', data),

  getRuntimeReleaseEvents: (limit = 10) =>
    api.get<RuntimeReleaseEvent[]>('/localization/runtime/releases/events', { params: { limit } }),

  getRuntimeReleaseHealth: () =>
    api.get<RuntimeReleaseHealth>('/localization/runtime/releases/health'),
};
